import { createAppRequest } from '@/services/request';
import { BizAppConfig } from 'config/application.manifest';
import { ensureValidAccessToken, getAccessToken } from '@/services/oauth2';
import type { ApiRequestOptions, ApiResponse } from './types/common';
import type {
  CommonSignedUrlResult,
  CommonUploadFilePayload,
  CommonUploadFileResult,
  ConfirmProjectStatusPayload,
  CreateProjectAiAnalysisPayload,
  CreateProjectFocusItemPayload,
  CreateProjectFromTemplatePayload,
  CreateProjectTaskDependencyPayload,
  CreateProjectTemplatePayload,
  CreateProjectResourceAssignmentPayload,
  DuplicateProjectTemplatePayload,
  GanttDependencyItem,
  GanttTaskItem,
  GlobalGanttData,
  GlobalGanttTaskItem,
  GlobalKanbanColumn,
  GlobalKanbanData,
  GlobalKanbanTaskItem,
  GlobalTaskRiskLevel,
  KanbanColumn,
  CreateProjectPayload,
  CreateProjectTaskPayload,
  CreateProjectPlanMilestonePayload,
  CreateProjectPlanStagePayload,
  MoveProjectTaskInKanbanPayload,
  MoveGlobalTaskInKanbanPayload,
  MyTaskItem,
  MyProjectRole,
  ProjectActivityItem,
  ProjectAiAnalysisItem,
  ProjectDashboardData,
  ProjectFocusItem,
  ProjectItem,
  ProjectIssueItem,
  ProjectIssuePageData,
  ProjectIssueSummary,
  ProjectIssueTaskRelationItem,
  ProjectIssueCategory,
  ProjectIssueLevel,
  ProjectIssueStatus,
  ProjectWorkbenchData,
  ProjectWorkbenchIssueItem,
  ProjectMemberItem,
  ProjectGanttData,
  ProjectOverviewData,
  ProjectFileFolderTreeItem,
  ProjectReadonlyTabData,
  ProjectOverviewLatestReport,
  ProjectWeeklyReviewData,
  ProjectReportItem,
  ProjectReportListResult,
  ProjectReportType,
  QueryProjectWeeklyReviewParams,
  QueryProjectReportsParams,
  ProjectReviewItem,
  ProjectReviewActionItem,
  ProjectAcceptanceItem,
  ProjectAcceptanceCreatePayload,
  ProjectCommunicationItem,
  ProjectCommunicationPayload,
  ProjectRiskItem,
  GlobalProjectRiskItem,
  ProjectResourceItem,
  ResourceCenterListItem,
  ResourceCenterRiskStatus,
  ProjectCalendarData,
  QueryProjectCalendarParams,
  ResourceCenterMemberDetail,
  ResourceCenterPageData,
  ResourceCenterQueryParams,
  CreateProjectReportPayload,
  ProjectTaskItem,
  ProjectTaskRecentActivityItem,
  ProjectTaskResourceSummary,
  ProjectTaskRiskSummary,
  ProjectPlanData,
  ProjectPlanMilestoneItem,
  ProjectPlanStageItem,
  ProjectTemplateItem,
  ProjectSubscriptionSubjectType,
  QueryGlobalGanttParams,
  QueryGlobalKanbanParams,
  QueryMyTasksParams,
  QueryProjectsParams,
  QueryProjectIssuesParams,
  QueryProjectTemplatesParams,
  QueryProjectCommunicationsParams,
  SaveProjectAsTemplatePayload,
  CreateProjectIssuePayload,
  UpdateProjectIssuePayload,
  UpdateProjectIssueStatusPayload,
  UpdateProjectIssueBlockedPayload,
  UpdateProjectIssueTasksPayload,
  UpdateProjectFocusItemPayload,
  UpdateProjectPayload,
  UpdateProjectTemplatePayload,
  UpdateProjectTaskPayload,
  UpdateProjectPlanMilestonePayload,
  UpdateProjectPlanStagePayload,
  UpdateProjectReportPayload,
} from './types/project-center';

const request = createAppRequest();

interface PageResult<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

const taskStatusFallbackOrder: Array<{
  key: ProjectTaskItem['status'];
  name: string;
}> = [
  { key: 'todo', name: '待开始' },
  { key: 'in_progress', name: '进行中' },
  { key: 'review', name: '待审核' },
  { key: 'done', name: '已完成' },
];

const pickString = (...values: Array<unknown>) => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value;
    }
  }
  return undefined;
};

const pickNumber = (...values: Array<unknown>) => {
  for (const value of values) {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === 'string' && value.trim()) {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
  }
  return undefined;
};

const pickBoolean = (...values: Array<unknown>) => {
  for (const value of values) {
    if (typeof value === 'boolean') {
      return value;
    }
  }
  return undefined;
};

const readStoredTenantUuid = () => {
  try {
    return localStorage.getItem('currentTenantUuid') || undefined;
  } catch {
    return undefined;
  }
};

const readStoredOrganizationUuid = () => {
  try {
    const userJson = localStorage.getItem('user');
    if (!userJson) {
      return undefined;
    }
    const user = JSON.parse(userJson) as {
      organizationUuid?: string;
      lastLoginOrganizationUuid?: string;
    };
    return user.organizationUuid || user.lastLoginOrganizationUuid || undefined;
  } catch {
    return undefined;
  }
};

const normalizeTaskStatus = (value: unknown): ProjectTaskItem['status'] => {
  if (value === 'todo' || value === 'in_progress' || value === 'review' || value === 'done') {
    return value;
  }
  if (value === 'not_started' || value === 'pending') {
    return 'todo';
  }
  if (value === 'doing' || value === 'processing') {
    return 'in_progress';
  }
  if (value === 'testing' || value === 'qa') {
    return 'review';
  }
  if (value === 'completed' || value === 'closed') {
    return 'done';
  }
  return 'todo';
};

const normalizeTaskUuid = (raw: Record<string, any>) =>
  pickString(raw.uuid, raw.taskUuid, raw.task_uuid, raw.taskId, raw.task_id, raw.id) || '';

const normalizeTaskPriority = (value: unknown): ProjectTaskItem['priority'] => {
  if (value === 'low' || value === 'medium' || value === 'high' || value === 'urgent') {
    return value;
  }
  return 'medium';
};

const normalizeActualHoursSourceStatus = (value: unknown) => {
  if (
    value === 'task_field' ||
    value === 'resource_assignment' ||
    value === 'mixed' ||
    value === 'missing' ||
    value === 'unknown'
  ) {
    return value;
  }
  return null;
};

const normalizeGlobalTaskRiskLevel = (value: unknown): GlobalKanbanTaskItem['riskLevel'] => {
  if (
    value === 'normal' ||
    value === 'low' ||
    value === 'medium' ||
    value === 'high' ||
    value === 'critical'
  ) {
    return value;
  }
  return undefined;
};

const normalizeProjectTaskItem = (rawTask: any): ProjectTaskItem => {
  const taskUuid = normalizeTaskUuid(rawTask);
  const startDate = pickString(rawTask.startDate, rawTask.start_date, rawTask.planStartDate);
  const endDate = pickString(
    rawTask.endDate,
    rawTask.end_date,
    rawTask.dueDate,
    rawTask.due_date,
    rawTask.planEndDate,
  );
  const createdAt = pickString(rawTask.createdAt, rawTask.created_at) || '';
  const updatedAt = pickString(rawTask.updatedAt, rawTask.updated_at) || createdAt;

  return {
    id: pickNumber(rawTask.id, rawTask.taskId, rawTask.task_id) || 0,
    uuid: taskUuid,
    taskUuid,
    taskId: pickString(rawTask.taskId, rawTask.task_id) || pickNumber(rawTask.taskId, rawTask.task_id, rawTask.id),
    projectUuid: pickString(rawTask.projectUuid, rawTask.project_uuid) || '',
    title: pickString(rawTask.title, rawTask.taskTitle, rawTask.name) || '未命名任务',
    description: pickString(rawTask.description, rawTask.content) || null,
    assigneeUuid:
      pickString(
        rawTask.assigneeUuid,
        rawTask.assignee_uuid,
        rawTask.ownerUuid,
        rawTask.executorUuid,
        rawTask.picUuid,
      ) || null,
    assigneeName:
      pickString(
        rawTask.assigneeName,
        rawTask.assignee_name,
        rawTask.ownerName,
        rawTask.owner_name,
        rawTask.executorName,
        rawTask.executor_name,
        rawTask.picName,
        rawTask.pic_name,
        rawTask.memberName,
        rawTask.member_name,
      ) || null,
    status: normalizeTaskStatus(rawTask.status),
    priority: normalizeTaskPriority(pickString(rawTask.priority, rawTask.level)),
    startDate: startDate || null,
    endDate: endDate || null,
    dueDate: pickString(rawTask.dueDate, rawTask.due_date) || endDate || null,
    estimatedHours: pickNumber(rawTask.estimatedHours, rawTask.estimateHours, rawTask.workHours) ?? null,
    actualHours: pickNumber(rawTask.actualHours, rawTask.loggedHours) ?? null,
    actualHoursSourceStatus: normalizeActualHoursSourceStatus(
      pickString(
        rawTask.actualHoursSourceStatus,
        rawTask.actual_hours_source_status,
        rawTask.hoursSourceStatus,
        rawTask.hours_source_status,
      ),
    ),
    parentTaskUuid:
      pickString(rawTask.parentTaskUuid, rawTask.parentTaskId, rawTask.parent_uuid, rawTask.parent_id) ||
      null,
    sortOrder: pickNumber(rawTask.sortOrder, rawTask.sort_order, rawTask.orderNo, rawTask.order_num) || 0,
    isKeyTask: pickBoolean(rawTask.isKeyTask, rawTask.is_key_task) || false,
    stageUuid: pickString(rawTask.stageUuid, rawTask.stage_uuid) || null,
    stageName: pickString(rawTask.stageName, rawTask.stage_name) || null,
    isDelayed:
      pickBoolean(rawTask.isDelayed, rawTask.is_delayed, rawTask.delayFlag, rawTask.delay_flag) || false,
    delayReason:
      pickString(rawTask.delayReason, rawTask.delay_reason, rawTask.overdueReason, rawTask.overdue_reason) || null,
    isOverdue: pickBoolean(rawTask.isOverdue, rawTask.is_overdue) || false,
    progress:
      pickNumber(rawTask.progress, rawTask.progressPercent, rawTask.percent, rawTask.progress_rate) ??
      null,
    baselineStartDate:
      pickString(rawTask.baselineStartDate, rawTask.baseline_start_date) || null,
    baselineEndDate: pickString(rawTask.baselineEndDate, rawTask.baseline_end_date) || null,
    displayGroup: pickString(rawTask.displayGroup, rawTask.display_group) || null,
    sourceAppUuid: pickString(rawTask.sourceAppUuid, rawTask.source_app_uuid) || null,
    sourceSceneUuid: pickString(rawTask.sourceSceneUuid, rawTask.source_scene_uuid) || null,
    sourceObjectUuid: pickString(rawTask.sourceObjectUuid, rawTask.source_object_uuid) || null,
    sourceType: pickString(rawTask.sourceType, rawTask.source_type) || null,
    createdBy: pickString(rawTask.createdBy, rawTask.created_by) || null,
    updatedBy: pickString(rawTask.updatedBy, rawTask.updated_by) || null,
    legacyTaskFound: pickBoolean(rawTask.legacyTaskFound, rawTask.legacy_task_found) || false,
    projectName: pickString(rawTask.projectName, rawTask.project_name) || null,
    boardColumnKey:
      (pickString(rawTask.boardColumnKey, rawTask.board_column_key) as ProjectTaskItem['boardColumnKey']) ||
      undefined,
    riskSummary:
      rawTask.riskSummary && typeof rawTask.riskSummary === 'object'
        ? {
            hasRisk: pickBoolean(rawTask.riskSummary.hasRisk, rawTask.riskSummary.has_risk) || false,
            riskCount:
              pickNumber(rawTask.riskSummary.riskCount, rawTask.riskSummary.risk_count) || 0,
            riskLevel:
              (pickString(
                rawTask.riskSummary.riskLevel,
                rawTask.riskSummary.risk_level,
              ) as ProjectTaskRiskSummary['riskLevel']) || null,
            risks: Array.isArray(rawTask.riskSummary.risks)
              ? rawTask.riskSummary.risks.map((item: any) => ({
                  riskUuid: pickString(item.riskUuid, item.risk_uuid, item.uuid) || '',
                  title: pickString(item.title) || '未命名风险',
                  riskType: normalizeProjectRiskType(pickString(item.riskType, item.risk_type)),
                  riskLevel: normalizeProjectRiskLevel(pickString(item.riskLevel, item.risk_level)),
                  isResolved: pickBoolean(item.isResolved, item.is_resolved) || false,
                }))
              : [],
          }
        : null,
    resourceSummary:
      rawTask.resourceSummary && typeof rawTask.resourceSummary === 'object'
        ? {
            assignmentCount:
              pickNumber(
                rawTask.resourceSummary.assignmentCount,
                rawTask.resourceSummary.assignment_count,
              ) || 0,
            allocationPercent:
              pickNumber(
                rawTask.resourceSummary.allocationPercent,
                rawTask.resourceSummary.allocation_percent,
              ) || 0,
            estimatedHours:
              pickNumber(
                rawTask.resourceSummary.estimatedHours,
                rawTask.resourceSummary.estimated_hours,
              ) || 0,
            assignments: Array.isArray(rawTask.resourceSummary.assignments)
              ? rawTask.resourceSummary.assignments.map((item: any) => ({
                  uuid: pickString(item.uuid, item.assignmentUuid, item.assignment_uuid) || '',
                  resourceUuid: pickString(item.resourceUuid, item.resource_uuid) || '',
                  resourceType: pickString(item.resourceType, item.resource_type) || 'member',
                  allocationPercent:
                    pickNumber(item.allocationPercent, item.allocation_percent) || 0,
                  estimatedHours: pickNumber(item.estimatedHours, item.estimated_hours) ?? null,
                  startDate: pickString(item.startDate, item.start_date) || null,
                  endDate: pickString(item.endDate, item.end_date) || null,
                }))
              : [],
          }
        : null,
    predecessors: Array.isArray(rawTask.predecessors)
      ? rawTask.predecessors.map((item: any) => ({
          taskUuid: pickString(item.taskUuid, item.task_uuid) || '',
          title: pickString(item.title) || '未命名任务',
          status: normalizeTaskStatus(pickString(item.status)),
          lagDays: pickNumber(item.lagDays, item.lag_days) || 0,
        }))
      : [],
    successors: Array.isArray(rawTask.successors)
      ? rawTask.successors.map((item: any) => ({
          taskUuid: pickString(item.taskUuid, item.task_uuid) || '',
          title: pickString(item.title) || '未命名任务',
          status: normalizeTaskStatus(pickString(item.status)),
          lagDays: pickNumber(item.lagDays, item.lag_days) || 0,
        }))
      : [],
    recentActivities: Array.isArray(rawTask.recentActivities)
      ? rawTask.recentActivities.map((item: any) => ({
          uuid: pickString(item.uuid) || '',
          action: pickString(item.action) || '',
          content: pickString(item.content) || '',
          actorUuid: pickString(item.actorUuid, item.actor_uuid) || '',
          createdAt: pickString(item.createdAt, item.created_at) || '',
        }))
      : [],
    createdAt,
    updatedAt,
  };
};

const normalizeGlobalKanbanTaskItem = (rawTask: any): GlobalKanbanTaskItem => {
  const task = normalizeProjectTaskItem(rawTask);
  return {
    ...task,
    projectName: pickString(rawTask.projectName, rawTask.project_name) || null,
    projectCode: pickString(rawTask.projectCode, rawTask.project_code) || null,
    isBlocked: pickBoolean(rawTask.isBlocked, rawTask.is_blocked) || false,
    riskLevel: normalizeGlobalTaskRiskLevel(pickString(rawTask.riskLevel, rawTask.risk_level)),
    departmentUuid: pickString(rawTask.departmentUuid, rawTask.department_uuid) || null,
  };
};

const normalizeProjectActivityItem = (rawActivity: any): ProjectActivityItem => ({
  id: pickNumber(rawActivity.id) || 0,
  uuid: pickString(rawActivity.uuid) || '',
  projectUuid: pickString(rawActivity.projectUuid, rawActivity.project_uuid) || '',
  actorUuid: pickString(rawActivity.actorUuid, rawActivity.actor_uuid) || '',
  actorName:
    pickString(
      rawActivity.actorName,
      rawActivity.actor_name,
      rawActivity.operatorName,
      rawActivity.operator_name,
      rawActivity.memberName,
      rawActivity.member_name,
    ) || null,
  action: pickString(rawActivity.action) || '',
  targetType: pickString(rawActivity.targetType, rawActivity.target_type) || '',
  targetUuid: pickString(rawActivity.targetUuid, rawActivity.target_uuid) || null,
  content: pickString(rawActivity.content, rawActivity.title) || '',
  metadata:
    rawActivity.metadata && typeof rawActivity.metadata === 'object' ? rawActivity.metadata : null,
  createdAt: pickString(rawActivity.createdAt, rawActivity.created_at) || '',
});

const normalizeProjectRiskLevel = (value: unknown): ProjectRiskItem['riskLevel'] => {
  if (value === 'low' || value === 'medium' || value === 'high' || value === 'critical') {
    return value;
  }
  return 'medium';
};

const normalizeProjectRiskType = (value: unknown): ProjectRiskItem['riskType'] => {
  if (
    value === 'task_overdue' ||
    value === 'project_overdue' ||
    value === 'resource_overload' ||
    value === 'project_inactive' ||
    value === 'task_blocked' ||
    value === 'resource_schedule_conflict'
  ) {
    return value;
  }
  return 'task_blocked';
};

const normalizeProjectRiskItem = (rawRisk: any): ProjectRiskItem => ({
  uuid: pickString(rawRisk.uuid, rawRisk.riskUuid, rawRisk.risk_uuid) || '',
  projectUuid: pickString(rawRisk.projectUuid, rawRisk.project_uuid) || '',
  riskType: normalizeProjectRiskType(pickString(rawRisk.riskType, rawRisk.risk_type)),
  riskLevel: normalizeProjectRiskLevel(pickString(rawRisk.riskLevel, rawRisk.risk_level)),
  title: pickString(rawRisk.title) || '未命名风险',
  description: pickString(rawRisk.description, rawRisk.content) || '',
  isResolved: pickBoolean(rawRisk.isResolved, rawRisk.is_resolved) || false,
  targetType: pickString(rawRisk.targetType, rawRisk.target_type) || null,
  targetUuid: pickString(rawRisk.targetUuid, rawRisk.target_uuid) || null,
  metadata: rawRisk.metadata && typeof rawRisk.metadata === 'object' ? rawRisk.metadata : null,
  createdAt: pickString(rawRisk.createdAt, rawRisk.created_at) || '',
});

const normalizeProjectIssueCategory = (value?: string): ProjectIssueCategory => {
  if (
    value === 'requirement' ||
    value === 'technical' ||
    value === 'resource' ||
    value === 'process' ||
    value === 'external' ||
    value === 'other'
  ) {
    return value;
  }
  return 'other';
};

const normalizeProjectIssueLevel = (value?: string): ProjectIssueLevel => {
  if (value === 'low' || value === 'medium' || value === 'high' || value === 'critical') {
    return value;
  }
  return 'medium';
};

const normalizeProjectIssueStatus = (value?: string): ProjectIssueStatus => {
  if (value === 'open' || value === 'in_progress' || value === 'resolved' || value === 'closed') {
    return value;
  }
  return 'open';
};

const normalizeProjectIssueTaskRelationItem = (rawItem: any): ProjectIssueTaskRelationItem => ({
  taskUuid: pickString(rawItem.taskUuid, rawItem.task_uuid, rawItem.uuid) || '',
  title: pickString(rawItem.title, rawItem.taskTitle, rawItem.task_title) || null,
  status: (pickString(rawItem.status) as ProjectTaskItem['status'] | undefined) || null,
});

const normalizeProjectIssueSummary = (rawSummary: any): ProjectIssueSummary => ({
  totalCount: pickNumber(rawSummary?.totalCount, rawSummary?.total_count) || 0,
  openCount: pickNumber(rawSummary?.openCount, rawSummary?.open_count) || 0,
  highPriorityCount:
    pickNumber(rawSummary?.highPriorityCount, rawSummary?.high_priority_count) || 0,
  blockedCount: pickNumber(rawSummary?.blockedCount, rawSummary?.blocked_count) || 0,
});

const normalizeProjectIssueItem = (rawItem: any): ProjectIssueItem => ({
  id: pickNumber(rawItem.id) || 0,
  uuid: pickString(rawItem.uuid, rawItem.issueUuid, rawItem.issue_uuid) || '',
  tenantUuid: pickString(rawItem.tenantUuid, rawItem.tenant_uuid) || null,
  projectUuid: pickString(rawItem.projectUuid, rawItem.project_uuid) || '',
  title: pickString(rawItem.title) || '未命名问题',
  description: pickString(rawItem.description, rawItem.content) || null,
  issueCategory: normalizeProjectIssueCategory(
    pickString(rawItem.issueCategory, rawItem.issue_category),
  ),
  issueLevel: normalizeProjectIssueLevel(
    pickString(rawItem.issueLevel, rawItem.issue_level),
  ),
  ownerUuid: pickString(rawItem.ownerUuid, rawItem.owner_uuid) || null,
  plannedResolveAt:
    pickString(rawItem.plannedResolveAt, rawItem.planned_resolve_at) || null,
  status: normalizeProjectIssueStatus(pickString(rawItem.status)),
  isBlocked: pickBoolean(rawItem.isBlocked, rawItem.is_blocked) || false,
  relatedTasks: Array.isArray(rawItem.relatedTasks)
    ? rawItem.relatedTasks.map(normalizeProjectIssueTaskRelationItem)
    : Array.isArray(rawItem.related_tasks)
      ? rawItem.related_tasks.map(normalizeProjectIssueTaskRelationItem)
      : [],
  createdBy: pickString(rawItem.createdBy, rawItem.created_by) || null,
  updatedBy: pickString(rawItem.updatedBy, rawItem.updated_by) || null,
  createdAt: pickString(rawItem.createdAt, rawItem.created_at) || '',
  updatedAt:
    pickString(rawItem.updatedAt, rawItem.updated_at, rawItem.createdAt, rawItem.created_at) || '',
});

const normalizeProjectStatus = (value: unknown): ProjectItem['status'] => {
  if (
    value === 'not_started' ||
    value === 'in_progress' ||
    value === 'paused' ||
    value === 'completed' ||
    value === 'closed' ||
    value === 'overdue'
  ) {
    return value;
  }
  if (value === 'todo' || value === 'pending') {
    return 'not_started';
  }
  if (value === 'doing' || value === 'processing') {
    return 'in_progress';
  }
  if (value === 'done') {
    return 'completed';
  }
  return 'not_started';
};

const normalizeProjectType = (value: unknown): ProjectItem['type'] => {
  if (typeof value === 'string' && value.trim()) {
    return value;
  }
  return 'custom';
};

const normalizeProjectHealthLevel = (value: unknown): ProjectItem['healthLevel'] => {
  if (value === 'healthy' || value === 'attention' || value === 'risk' || value === 'critical') {
    return value;
  }
  return 'healthy';
};

const normalizeProjectProgressStatus = (value: unknown): ProjectItem['progressStatus'] => {
  if (
    value === 'not_started' ||
    value === 'on_track' ||
    value === 'at_risk' ||
    value === 'overdue' ||
    value === 'completed'
  ) {
    return value;
  }
  if (value === 'in_progress') {
    return 'on_track';
  }
  return 'not_started';
};

const normalizeProjectMemberRole = (value: unknown): ProjectMemberItem['role'] => {
  if (value === 'owner' || value === 'manager' || value === 'member' || value === 'viewer') {
    return value;
  }
  if (value === 'admin') {
    return 'manager';
  }
  return 'member';
};

const normalizeMyProjectRole = (value: unknown): MyProjectRole | undefined => {
  if (value === 'owner' || value === 'member' || value === 'owner_member') {
    return value;
  }
  if (value === 'manager' || value === 'viewer' || value === 'admin') {
    return 'member';
  }
  return undefined;
};

const normalizeProjectMemberItem = (rawMember: any): ProjectMemberItem | null => {
  const memberUuid =
    pickString(
      rawMember.memberUuid,
      rawMember.member_uuid,
      rawMember.uuid,
      rawMember.userUuid,
      rawMember.user_uuid,
      rawMember.id,
    ) || '';

  if (!memberUuid) {
    return null;
  }

  return {
    uuid:
      pickString(
        rawMember.uuid,
        rawMember.projectMemberUuid,
        rawMember.project_member_uuid,
        rawMember.relationUuid,
        rawMember.relation_uuid,
      ) || undefined,
    memberUuid,
    role: normalizeProjectMemberRole(pickString(rawMember.role, rawMember.memberRole)),
    joinedAt:
      pickString(
        rawMember.joinedAt,
        rawMember.joined_at,
        rawMember.createdAt,
        rawMember.created_at,
      ) || undefined,
  };
};

const normalizeProjectItem = (rawProject: any): ProjectItem => {
  const rawMembers = Array.isArray(rawProject?.projectMembers)
    ? rawProject.projectMembers
    : Array.isArray(rawProject?.project_members)
      ? rawProject.project_members
      : Array.isArray(rawProject?.members)
        ? rawProject.members
        : Array.isArray(rawProject?.memberList)
          ? rawProject.memberList
          : [];

  const projectMembers = rawMembers
    .map(normalizeProjectMemberItem)
    .filter((item: ProjectMemberItem | null): item is ProjectMemberItem => Boolean(item));

  const ownerUuid =
    pickString(
      rawProject.ownerUuid,
      rawProject.owner_uuid,
      rawProject.ownerMemberUuid,
      rawProject.owner_member_uuid,
    ) ||
    projectMembers.find((item: ProjectMemberItem) => item.role === 'owner')?.memberUuid ||
    '';

  const memberMap = new Map<string, ProjectMemberItem>();
  projectMembers.forEach((item: ProjectMemberItem) => {
    memberMap.set(item.memberUuid, item);
  });
  if (ownerUuid && !memberMap.has(ownerUuid)) {
    memberMap.set(ownerUuid, {
      memberUuid: ownerUuid,
      role: 'owner',
    });
  }

  const createdAt = pickString(rawProject.createdAt, rawProject.created_at) || '';
  const updatedAt = pickString(rawProject.updatedAt, rawProject.updated_at) || createdAt;

  return {
    id: pickNumber(rawProject.id, rawProject.projectId, rawProject.project_id) || 0,
    uuid: pickString(rawProject.uuid, rawProject.projectUuid, rawProject.project_uuid) || '',
    name: pickString(rawProject.name, rawProject.projectName, rawProject.title) || '未命名项目',
    code: pickString(rawProject.code, rawProject.projectCode, rawProject.project_code) || null,
    type: normalizeProjectType(pickString(rawProject.type, rawProject.projectType, rawProject.project_type)),
    status: normalizeProjectStatus(pickString(rawProject.status, rawProject.projectStatus, rawProject.project_status)),
    statusReason: pickString(rawProject.statusReason, rawProject.status_reason) || null,
    statusConfirmedBy:
      pickString(rawProject.statusConfirmedBy, rawProject.status_confirmed_by) || null,
    statusConfirmedAt:
      pickString(rawProject.statusConfirmedAt, rawProject.status_confirmed_at) || null,
    statusUpdatedBy: pickString(rawProject.statusUpdatedBy, rawProject.status_updated_by) || null,
    statusUpdatedAt: pickString(rawProject.statusUpdatedAt, rawProject.status_updated_at) || null,
    priority: normalizeTaskPriority(pickString(rawProject.priority, rawProject.level, rawProject.projectPriority)),
    ownerUuid,
    startDate:
      pickString(rawProject.startDate, rawProject.start_date, rawProject.planStartDate, rawProject.plan_start_date) ||
      null,
    endDate:
      pickString(rawProject.endDate, rawProject.end_date, rawProject.planEndDate, rawProject.plan_end_date) ||
      null,
    actualStartDate:
      pickString(rawProject.actualStartDate, rawProject.actual_start_date) || null,
    actualEndDate: pickString(rawProject.actualEndDate, rawProject.actual_end_date) || null,
    progress: pickNumber(rawProject.progress, rawProject.progressPercent, rawProject.progress_rate) || 0,
    healthScore: pickNumber(rawProject.healthScore, rawProject.health_score) || 0,
    healthLevel: normalizeProjectHealthLevel(
      pickString(rawProject.healthLevel, rawProject.health_level),
    ),
    openRiskCount: pickNumber(rawProject.openRiskCount, rawProject.open_risk_count, rawProject.riskCount) || 0,
    highRiskCount: pickNumber(rawProject.highRiskCount, rawProject.high_risk_count) || 0,
    overdueTaskCount:
      pickNumber(rawProject.overdueTaskCount, rawProject.overdue_task_count) || 0,
    taskTotalCount: pickNumber(rawProject.taskTotalCount, rawProject.task_total_count) || 0,
    taskDoneCount: pickNumber(rawProject.taskDoneCount, rawProject.task_done_count) || 0,
    milestoneCount: pickNumber(rawProject.milestoneCount, rawProject.milestone_count) || 0,
    completedMilestoneCount:
      pickNumber(rawProject.completedMilestoneCount, rawProject.completed_milestone_count) || 0,
    isOverdue: pickBoolean(rawProject.isOverdue, rawProject.is_overdue) || false,
    daysRemaining: pickNumber(rawProject.daysRemaining, rawProject.days_remaining) ?? null,
    progressStatus: normalizeProjectProgressStatus(
      pickString(rawProject.progressStatus, rawProject.progress_status),
    ),
    description: pickString(rawProject.description, rawProject.content) || null,
    isArchived: pickBoolean(rawProject.isArchived, rawProject.is_archived) || false,
    projectMembers: memberMap.size ? Array.from(memberMap.values()) : undefined,
    myProjectRole: normalizeMyProjectRole(
      pickString(rawProject.myProjectRole, rawProject.my_project_role),
    ),
    createdAt,
    updatedAt,
  };
};

const normalizeOverviewSummary = (rawSummary: any, project?: ProjectItem): ProjectOverviewData['summary'] => ({
  progress: pickNumber(rawSummary?.progress, project?.progress) || 0,
  healthScore: pickNumber(rawSummary?.healthScore, rawSummary?.health_score, project?.healthScore) || 0,
  healthLevel:
    (pickString(
      rawSummary?.healthLevel,
      rawSummary?.health_level,
      project?.healthLevel,
    ) as ProjectOverviewData['summary']['healthLevel']) || 'healthy',
  milestoneProgress:
    pickNumber(
      rawSummary?.milestoneProgress,
      rawSummary?.milestone_progress,
      project?.milestoneCount
        ? Math.round(((project.completedMilestoneCount || 0) / project.milestoneCount) * 100)
        : 0,
    ) || 0,
  riskCount:
    pickNumber(rawSummary?.riskCount, rawSummary?.risk_count, project?.openRiskCount) || 0,
  blockedTaskCount:
    pickNumber(rawSummary?.blockedTaskCount, rawSummary?.blocked_task_count) || 0,
  overdueTaskCount:
    pickNumber(rawSummary?.overdueTaskCount, rawSummary?.overdue_task_count, project?.overdueTaskCount) || 0,
  upcomingTaskCount:
    pickNumber(rawSummary?.upcomingTaskCount, rawSummary?.upcoming_task_count) || 0,
  stageCount: pickNumber(rawSummary?.stageCount, rawSummary?.stage_count) || 0,
  currentStageName:
    pickString(rawSummary?.currentStageName, rawSummary?.current_stage_name) || null,
  issueCount: pickNumber(rawSummary?.issueCount, rawSummary?.issue_count) || 0,
  highPriorityIssueCount:
    pickNumber(rawSummary?.highPriorityIssueCount, rawSummary?.high_priority_issue_count) || 0,
  blockedIssueCount:
    pickNumber(rawSummary?.blockedIssueCount, rawSummary?.blocked_issue_count) || 0,
});

const normalizeProjectOverviewData = (
  rawData: any,
  project?: ProjectItem,
  risks?: ProjectRiskItem[],
  activities?: ProjectActivityItem[],
): ProjectOverviewData => {
  const currentRisks = Array.isArray(rawData?.currentRisks)
    ? rawData.currentRisks.map(normalizeProjectRiskItem)
    : Array.isArray(rawData?.current_risks)
      ? rawData.current_risks.map(normalizeProjectRiskItem)
      : risks || [];

  const recentActivities = Array.isArray(rawData?.recentActivities)
    ? rawData.recentActivities.map(normalizeProjectActivityItem)
    : Array.isArray(rawData?.recent_activities)
      ? rawData.recent_activities.map(normalizeProjectActivityItem)
      : activities || [];

  return {
    summary: normalizeOverviewSummary(rawData?.summary, project),
    todayFocusTasks: Array.isArray(rawData?.todayFocusTasks)
      ? rawData.todayFocusTasks.map(normalizeProjectTaskItem)
      : Array.isArray(rawData?.today_focus_tasks)
        ? rawData.today_focus_tasks.map(normalizeProjectTaskItem)
        : [],
    currentRisks,
    upcomingTasks: Array.isArray(rawData?.upcomingTasks)
      ? rawData.upcomingTasks.map(normalizeProjectTaskItem)
      : Array.isArray(rawData?.upcoming_tasks)
        ? rawData.upcoming_tasks.map(normalizeProjectTaskItem)
        : [],
    milestones: Array.isArray(rawData?.milestones)
      ? rawData.milestones.map((item: any) => {
          const task = normalizeProjectTaskItem(item);
          return {
            uuid: pickString(item.uuid, item.milestoneUuid, item.milestone_uuid) || task.uuid,
            taskUuid: pickString(item.taskUuid, item.task_uuid) || task.uuid || null,
            title: task.title,
            status: task.status,
            ownerUuid:
              pickString(item.ownerUuid, item.owner_uuid, item.assigneeUuid, item.assignee_uuid) ||
              task.assigneeUuid ||
              null,
            stageUuid: pickString(item.stageUuid, item.stage_uuid) || task.stageUuid || null,
            stageName: pickString(item.stageName, item.stage_name) || task.stageName || null,
            dueDate: task.dueDate || null,
            endDate: task.endDate || null,
            progress: task.progress ?? 0,
            isOverdue: task.isOverdue || false,
          };
        })
      : [],
    stages: Array.isArray(rawData?.stages)
      ? rawData.stages.map((item: any) => ({
          uuid: pickString(item.uuid, item.stageUuid, item.stage_uuid) || '',
          title: pickString(item.title, item.name, item.stageName, item.stage_name) || '未命名阶段',
          status: pickString(item.status) || null,
          startDate: pickString(item.startDate, item.start_date) || null,
          endDate: pickString(item.endDate, item.end_date) || null,
          taskCount: pickNumber(item.taskCount, item.task_count) || 0,
          overdueTaskCount:
            pickNumber(
              item.overdueTaskCount,
              item.overdue_task_count,
              item.delayedTaskCount,
              item.delayed_task_count,
            ) || 0,
        }))
      : [],
    recentMilestones: Array.isArray(rawData?.recentMilestones)
      ? rawData.recentMilestones.map((item: any) => {
          const task = normalizeProjectTaskItem(item);
          return {
            uuid: pickString(item.uuid, item.milestoneUuid, item.milestone_uuid) || task.uuid,
            taskUuid: pickString(item.taskUuid, item.task_uuid) || task.uuid || null,
            title: task.title,
            status: task.status,
            ownerUuid:
              pickString(item.ownerUuid, item.owner_uuid, item.assigneeUuid, item.assignee_uuid) ||
              task.assigneeUuid ||
              null,
            stageUuid: pickString(item.stageUuid, item.stage_uuid) || task.stageUuid || null,
            stageName: pickString(item.stageName, item.stage_name) || task.stageName || null,
            dueDate: task.dueDate || null,
            endDate: task.endDate || null,
            progress: task.progress ?? 0,
            isOverdue: task.isOverdue || false,
          };
        })
      : [],
    recentFileChanges: Array.isArray(rawData?.recentFileChanges)
      ? rawData.recentFileChanges.map((item: any) => ({
          uuid: pickString(item.uuid, item.changeUuid, item.change_uuid) || '',
          title: pickString(item.title, item.name) || '未命名变更',
          action: pickString(item.action) || null,
          operatorUuid: pickString(item.operatorUuid, item.operator_uuid, item.actorUuid) || null,
          operatorName:
            pickString(
              item.operatorName,
              item.operator_name,
              item.actorName,
              item.actor_name,
              item.memberName,
              item.member_name,
            ) || null,
          updatedAt: pickString(item.updatedAt, item.updated_at, item.createdAt, item.created_at) || '',
          targetUuid: pickString(item.targetUuid, item.target_uuid) || null,
        }))
      : Array.isArray(rawData?.recent_file_changes)
        ? rawData.recent_file_changes.map((item: any) => ({
            uuid: pickString(item.uuid, item.changeUuid, item.change_uuid) || '',
            title: pickString(item.title, item.name) || '未命名变更',
            action: pickString(item.action) || null,
            operatorUuid: pickString(item.operatorUuid, item.operator_uuid, item.actorUuid) || null,
            operatorName:
              pickString(
                item.operatorName,
                item.operator_name,
                item.actorName,
                item.actor_name,
                item.memberName,
                item.member_name,
              ) || null,
            updatedAt:
              pickString(item.updatedAt, item.updated_at, item.createdAt, item.created_at) || '',
            targetUuid: pickString(item.targetUuid, item.target_uuid) || null,
        }))
      : Array.isArray(rawData?.recentFileContractChanges)
        ? rawData.recentFileContractChanges
            .filter((item: any) => pickString(item.type) !== 'contract')
            .map((item: any) => ({
              uuid: pickString(item.uuid, item.changeUuid, item.change_uuid) || '',
              title: pickString(item.title, item.name) || '未命名变更',
              action: pickString(item.action) || null,
              operatorUuid: pickString(item.operatorUuid, item.operator_uuid, item.actorUuid) || null,
              operatorName:
                pickString(
                  item.operatorName,
                  item.operator_name,
                  item.actorName,
                  item.actor_name,
                  item.memberName,
                  item.member_name,
                ) || null,
              updatedAt:
                pickString(item.updatedAt, item.updated_at, item.createdAt, item.created_at) || '',
              targetUuid: pickString(item.targetUuid, item.target_uuid) || null,
            }))
        : Array.isArray(rawData?.recent_file_contract_changes)
          ? rawData.recent_file_contract_changes
              .filter((item: any) => pickString(item.type) !== 'contract')
              .map((item: any) => ({
                uuid: pickString(item.uuid, item.changeUuid, item.change_uuid) || '',
                title: pickString(item.title, item.name) || '未命名变更',
                action: pickString(item.action) || null,
                operatorUuid: pickString(item.operatorUuid, item.operator_uuid, item.actorUuid) || null,
                operatorName:
                  pickString(
                    item.operatorName,
                    item.operator_name,
                    item.actorName,
                    item.actor_name,
                    item.memberName,
                    item.member_name,
                  ) || null,
                updatedAt:
                  pickString(item.updatedAt, item.updated_at, item.createdAt, item.created_at) || '',
                targetUuid: pickString(item.targetUuid, item.target_uuid) || null,
              }))
          : [],
    memberLoadSummary: Array.isArray(rawData?.memberLoadSummary)
      ? rawData.memberLoadSummary.map((item: any) => ({
          memberUuid: pickString(item.memberUuid, item.resourceUuid, item.member_uuid) || '',
          memberName: pickString(item.memberName, item.resourceName, item.member_name) || null,
          loadPercent: pickNumber(item.loadPercent, item.load_percent) || 0,
          taskCount: pickNumber(item.taskCount, item.task_count) || 0,
          estimatedHours: pickNumber(item.estimatedHours, item.estimated_hours) ?? null,
          loadStatus:
            (pickString(item.loadStatus, item.load_status) as ProjectOverviewData['memberLoadSummary'][number]['loadStatus']) ||
            null,
        }))
      : Array.isArray(rawData?.member_load_summary)
        ? rawData.member_load_summary.map((item: any) => ({
            memberUuid: pickString(item.memberUuid, item.resourceUuid, item.member_uuid) || '',
            memberName: pickString(item.memberName, item.resourceName, item.member_name) || null,
            loadPercent: pickNumber(item.loadPercent, item.load_percent) || 0,
            taskCount: pickNumber(item.taskCount, item.task_count) || 0,
            estimatedHours: pickNumber(item.estimatedHours, item.estimated_hours) ?? null,
            loadStatus:
            (pickString(item.loadStatus, item.load_status) as ProjectOverviewData['memberLoadSummary'][number]['loadStatus']) ||
            null,
          }))
        : [],
    issueSummary: rawData?.issueSummary
      ? normalizeProjectIssueSummary(rawData.issueSummary)
      : rawData?.issue_summary
        ? normalizeProjectIssueSummary(rawData.issue_summary)
        : undefined,
    latestReport: rawData?.latestReport
      ? normalizeProjectReportSummary(rawData.latestReport)
      : rawData?.latest_report
        ? normalizeProjectReportSummary(rawData.latest_report)
        : rawData?.latestWeeklyReport
          ? normalizeProjectReportSummary(rawData.latestWeeklyReport)
          : rawData?.latest_weekly_report
            ? normalizeProjectReportSummary(rawData.latest_weekly_report)
            : null,
    latestWeeklyReport: rawData?.latestReport
      ? normalizeProjectReportSummary(rawData.latestReport)
      : rawData?.latest_report
        ? normalizeProjectReportSummary(rawData.latest_report)
        : rawData?.latestWeeklyReport
          ? normalizeProjectReportSummary(rawData.latestWeeklyReport)
          : rawData?.latest_weekly_report
            ? normalizeProjectReportSummary(rawData.latest_weekly_report)
            : null,
    recentActivities,
  };
};

const normalizeProjectReportItem = (rawData: any): ProjectReportItem => ({
  id: pickNumber(rawData?.id) || undefined,
  uuid: pickString(rawData?.uuid) || '',
  tenantUuid: pickString(rawData?.tenantUuid, rawData?.tenant_uuid) || undefined,
  projectUuid: pickString(rawData?.projectUuid, rawData?.project_uuid) || '',
  reportType:
    (pickString(rawData?.reportType, rawData?.report_type) as ProjectReportItem['reportType']) ||
    'weekly',
  periodKey: pickString(rawData?.periodKey, rawData?.period_key) || '',
  periodStart: pickString(rawData?.periodStart, rawData?.period_start) || '',
  periodEnd: pickString(rawData?.periodEnd, rawData?.period_end) || '',
  reportContent: pickString(rawData?.reportContent, rawData?.report_content) || '',
  status:
    (pickString(rawData?.status) as ProjectReportItem['status']) || 'draft',
  createdBy: pickString(rawData?.createdBy, rawData?.created_by) || '',
  modelName: pickString(rawData?.modelName, rawData?.model_name) || null,
  generatedAt: pickString(rawData?.generatedAt, rawData?.generated_at) || null,
  generatedBy: pickString(rawData?.generatedBy, rawData?.generated_by) || null,
  reportProfileUuid:
    pickString(rawData?.reportProfileUuid, rawData?.report_profile_uuid) || null,
  inputSnapshot:
    (rawData?.inputSnapshot as Record<string, unknown> | undefined) ||
    (rawData?.input_snapshot as Record<string, unknown> | undefined) ||
    null,
  submittedAt: pickString(rawData?.submittedAt, rawData?.submitted_at) || null,
  createdAt: pickString(rawData?.createdAt, rawData?.created_at) || '',
  updatedAt: pickString(rawData?.updatedAt, rawData?.updated_at) || '',
});

const normalizeProjectReportSummary = (rawData: any): ProjectOverviewLatestReport => ({
  uuid: pickString(rawData?.uuid) || '',
  reportType:
    (pickString(rawData?.reportType, rawData?.report_type) as ProjectOverviewLatestReport['reportType']) ||
    'weekly',
  periodKey: pickString(rawData?.periodKey, rawData?.period_key) || '',
  periodStart: pickString(rawData?.periodStart, rawData?.period_start) || '',
  periodEnd: pickString(rawData?.periodEnd, rawData?.period_end) || '',
  reportContent: pickString(rawData?.reportContent, rawData?.report_content) || '',
  status:
    (pickString(rawData?.status) as ProjectOverviewLatestReport['status']) || 'draft',
  modelName: pickString(rawData?.modelName, rawData?.model_name) || null,
  generatedAt: pickString(rawData?.generatedAt, rawData?.generated_at) || null,
  submittedAt: pickString(rawData?.submittedAt, rawData?.submitted_at) || null,
  createdAt: pickString(rawData?.createdAt, rawData?.created_at) || '',
});

const normalizeProjectPlanStage = (item: any): ProjectPlanStageItem => ({
  uuid: pickString(item.uuid) || '',
  projectUuid: pickString(item.projectUuid, item.project_uuid) || '',
  name: pickString(item.name, item.title) || '未命名阶段',
  description: pickString(item.description) || null,
  startDate: pickString(item.startDate, item.start_date) || null,
  endDate: pickString(item.endDate, item.end_date) || null,
  sortOrder: pickNumber(item.sortOrder, item.sort_order) || 0,
  ownerUuid: pickString(item.ownerUuid, item.owner_uuid) || null,
  taskCount: pickNumber(item.taskCount, item.task_count) || 0,
  delayedTaskCount: pickNumber(item.delayedTaskCount, item.delayed_task_count) || 0,
});

const normalizeProjectPlanMilestone = (item: any): ProjectPlanMilestoneItem => ({
  uuid: pickString(item.uuid) || '',
  projectUuid: pickString(item.projectUuid, item.project_uuid) || '',
  stageUuid: pickString(item.stageUuid, item.stage_uuid) || null,
  name: pickString(item.name, item.title) || '未命名里程碑',
  description: pickString(item.description) || null,
  plannedDate: pickString(item.plannedDate, item.planned_date) || null,
  baselineDate: pickString(item.baselineDate, item.baseline_date) || null,
  actualDate: pickString(item.actualDate, item.actual_date) || null,
  status:
    (pickString(item.status) as ProjectPlanMilestoneItem['status'] | undefined) || 'not_started',
  requiresAcceptance: pickBoolean(item.requiresAcceptance, item.requires_acceptance) || false,
  delayReason: pickString(item.delayReason, item.delay_reason) || null,
  taskUuids: Array.isArray(item.taskUuids)
    ? item.taskUuids.filter((value: unknown): value is string => typeof value === 'string')
    : Array.isArray(item.task_uuids)
      ? item.task_uuids.filter((value: unknown): value is string => typeof value === 'string')
      : [],
  acceptanceUuids: Array.isArray(item.acceptanceUuids)
    ? item.acceptanceUuids.filter((value: unknown): value is string => typeof value === 'string')
    : Array.isArray(item.acceptance_uuids)
      ? item.acceptance_uuids.filter((value: unknown): value is string => typeof value === 'string')
      : [],
  ownerUuid: pickString(item.ownerUuid, item.owner_uuid) || null,
  sortOrder: pickNumber(item.sortOrder, item.sort_order) || 0,
});

const normalizeProjectPlanData = (rawData: any): ProjectPlanData => ({
  stages: Array.isArray(rawData?.stages) ? rawData.stages.map(normalizeProjectPlanStage) : [],
  milestones: Array.isArray(rawData?.milestones)
    ? rawData.milestones.map(normalizeProjectPlanMilestone)
    : [],
  summary: {
    stageCount: pickNumber(rawData?.summary?.stageCount, rawData?.summary?.stage_count) || 0,
    milestoneCount:
      pickNumber(rawData?.summary?.milestoneCount, rawData?.summary?.milestone_count) || 0,
    delayedTaskCount:
      pickNumber(rawData?.summary?.delayedTaskCount, rawData?.summary?.delayed_task_count) || 0,
    currentStage: rawData?.summary?.currentStage
      ? normalizeProjectPlanStage(rawData.summary.currentStage)
      : null,
    recentMilestones: Array.isArray(rawData?.summary?.recentMilestones)
      ? rawData.summary.recentMilestones.map(normalizeProjectPlanMilestone)
      : [],
  },
});

const normalizeProjectFocusItem = (rawItem: any): ProjectFocusItem => ({
  uuid: pickString(rawItem.uuid) || '',
  projectUuid: pickString(rawItem.projectUuid, rawItem.project_uuid) || '',
  title: pickString(rawItem.title) || '未命名重点',
  sourceType: (pickString(rawItem.sourceType, rawItem.source_type) as ProjectFocusItem['sourceType']) || 'manual',
  sourceUuid: pickString(rawItem.sourceUuid, rawItem.source_uuid) || null,
  priority: pickNumber(rawItem.priority) || 50,
  status: (pickString(rawItem.status) as ProjectFocusItem['status']) || 'candidate',
  summary: pickString(rawItem.summary) || null,
  reason: pickString(rawItem.reason) || null,
  ownerUuid: pickString(rawItem.ownerUuid, rawItem.owner_uuid) || null,
  confirmedBy: pickString(rawItem.confirmedBy, rawItem.confirmed_by) || null,
  confirmedAt: pickString(rawItem.confirmedAt, rawItem.confirmed_at) || null,
  createdBy: pickString(rawItem.createdBy, rawItem.created_by) || '',
  updatedBy: pickString(rawItem.updatedBy, rawItem.updated_by) || null,
  createdAt: pickString(rawItem.createdAt, rawItem.created_at) || '',
  updatedAt: pickString(rawItem.updatedAt, rawItem.updated_at) || '',
});

const normalizeProjectFileFolderTreeItem = (rawItem: any): ProjectFileFolderTreeItem => ({
  uuid: pickString(rawItem.uuid) || '',
  folderName: pickString(rawItem.folderName, rawItem.folder_name, rawItem.name) || '未命名文件夹',
  parentFolderUuid: pickString(rawItem.parentFolderUuid, rawItem.parent_folder_uuid) || null,
  sortOrder: pickNumber(rawItem.sortOrder, rawItem.sort_order) || 0,
  fileCount: pickNumber(rawItem.fileCount, rawItem.file_count) || 0,
  children: Array.isArray(rawItem.children)
    ? rawItem.children.map(normalizeProjectFileFolderTreeItem)
    : [],
});

const normalizeProjectReadonlyTabData = (rawData: any): ProjectReadonlyTabData => ({
  readonly: pickBoolean(rawData?.readonly) ?? false,
  sourceStatus:
    rawData?.sourceStatus === 'database' || rawData?.sourceStatus === 'activity'
      ? rawData.sourceStatus
      : 'not_connected',
  currentFolderUuid: pickString(rawData?.currentFolderUuid, rawData?.current_folder_uuid) || null,
  currentFolderName: pickString(rawData?.currentFolderName, rawData?.current_folder_name) || null,
  folders: Array.isArray(rawData?.folders)
    ? rawData.folders.map(normalizeProjectFileFolderTreeItem)
    : [],
  emptyReason: pickString(rawData?.emptyReason, rawData?.empty_reason) || null,
  items: Array.isArray(rawData?.items)
    ? rawData.items.map((item: any) => ({
        uuid: pickString(item.uuid) || '',
        title: pickString(item.title, item.name, item.fileName, item.file_name) || '未命名文件',
        action: pickString(item.action) || null,
        operatorUuid: pickString(item.operatorUuid, item.operator_uuid, item.actorUuid) || null,
        operatorName:
          pickString(
            item.operatorName,
            item.operator_name,
            item.actorName,
            item.actor_name,
            item.memberName,
            item.member_name,
          ) || null,
        targetType: pickString(item.targetType, item.target_type) || null,
        targetUuid: pickString(item.targetUuid, item.target_uuid) || null,
        fileKey:
          pickString(
            item.fileKey,
            item.file_key,
            item.objectKey,
            item.object_key,
            item.targetUuid,
            item.target_uuid,
          ) || null,
        fileName: pickString(item.fileName, item.file_name, item.title, item.name) || null,
        contentType: pickString(item.contentType, item.content_type, item.mimeType, item.mime_type) || null,
        fileSize: pickNumber(item.fileSize, item.file_size, item.size) ?? null,
        folderUuid: pickString(item.folderUuid, item.folder_uuid) || null,
        folderName: pickString(item.folderName, item.folder_name) || null,
        updatedAt: pickString(item.updatedAt, item.updated_at, item.createdAt, item.created_at) || '',
      }))
    : [],
  summary: {
    totalFileCount:
      pickNumber(
        rawData?.summary?.totalFileCount,
        rawData?.summary?.total_file_count,
        rawData?.totalFileCount,
        rawData?.total_file_count,
      ) || 0,
    currentFolderFileCount:
      pickNumber(
        rawData?.summary?.currentFolderFileCount,
        rawData?.summary?.current_folder_file_count,
        rawData?.currentFolderFileCount,
        rawData?.current_folder_file_count,
      ) || 0,
    latestUpdatedAt: pickString(
      rawData?.summary?.latestUpdatedAt,
      rawData?.summary?.latest_updated_at,
      rawData?.latestUpdatedAt,
      rawData?.latest_updated_at,
    ) || null,
  },
});

const normalizeProjectWorkbenchData = (rawData: any): ProjectWorkbenchData => {
  const myTasks = Array.isArray(rawData?.myTasks)
    ? rawData.myTasks.map(normalizeProjectTaskItem)
    : [];
  const myProjects = Array.isArray(rawData?.myProjects)
    ? rawData.myProjects.map(normalizeProjectItem)
    : [];
  const recentActivities = Array.isArray(rawData?.recentActivities)
    ? rawData.recentActivities.map(normalizeProjectActivityItem)
    : [];
  const todoSummaryFromApi = rawData?.todoSummary;
  const projectSummaryFromApi = rawData?.projectSummary;
  const summary = rawData?.summary;
  const rawTodaySchedule = rawData?.todaySchedule;
  const scheduleItems = Array.isArray(rawTodaySchedule?.items)
    ? rawTodaySchedule.items
    : Array.isArray(rawTodaySchedule)
      ? rawTodaySchedule
      : [];

  return {
    todoSummary: {
      total:
        pickNumber(
          todoSummaryFromApi?.total,
          summary?.myOpenTaskCount,
          summary?.my_open_task_count,
        ) || myTasks.length,
      todo:
        pickNumber(todoSummaryFromApi?.todo, todoSummaryFromApi?.notStartedCount) ||
        myTasks.filter((item: MyTaskItem) => item.status === 'todo').length,
      inProgress:
        pickNumber(todoSummaryFromApi?.inProgress, todoSummaryFromApi?.in_progress) ||
        myTasks.filter((item: MyTaskItem) => item.status === 'in_progress').length,
      review:
        pickNumber(todoSummaryFromApi?.review, todoSummaryFromApi?.reviewCount) ||
        myTasks.filter((item: MyTaskItem) => item.status === 'review').length,
      overdue:
        pickNumber(todoSummaryFromApi?.overdue, todoSummaryFromApi?.overdueCount) ||
        myTasks.filter((item: MyTaskItem) => Boolean(item.isOverdue)).length,
    },
    projectSummary: {
      participatingCount:
        pickNumber(
          projectSummaryFromApi?.participatingCount,
          summary?.projectCount,
          summary?.project_count,
        ) || myProjects.length,
      myHighPriorityIssueCount:
        pickNumber(
          projectSummaryFromApi?.myHighPriorityIssueCount,
          projectSummaryFromApi?.my_high_priority_issue_count,
          summary?.myHighPriorityIssueCount,
          summary?.my_high_priority_issue_count,
        ) || 0,
      pendingWeeklyReportCount:
        pickNumber(
          projectSummaryFromApi?.pendingWeeklyReportCount,
          projectSummaryFromApi?.pending_weekly_report_count,
          summary?.pendingWeeklyReportCount,
          summary?.pending_weekly_report_count,
        ) || 0,
      pendingWorkHourCount:
        pickNumber(
          projectSummaryFromApi?.pendingWorkHourCount,
          projectSummaryFromApi?.pending_work_hour_count,
          summary?.pendingWorkHourCount,
          summary?.pending_work_hour_count,
        ) || 0,
    },
    myTasks,
    myProjects,
    myHighPriorityIssues: Array.isArray(rawData?.myHighPriorityIssues)
      ? rawData.myHighPriorityIssues.map(
          (item: any): ProjectWorkbenchIssueItem => ({
            uuid: pickString(item.uuid, item.issueUuid, item.issue_uuid) || '',
            projectUuid: pickString(item.projectUuid, item.project_uuid) || '',
            title: pickString(item.title) || '未命名问题',
            issueLevel: normalizeProjectIssueLevel(
              pickString(item.issueLevel, item.issue_level),
            ),
            status: normalizeProjectIssueStatus(pickString(item.status)),
            isBlocked: pickBoolean(item.isBlocked, item.is_blocked) || false,
            plannedResolveAt:
              pickString(item.plannedResolveAt, item.planned_resolve_at) || null,
            ownerUuid: pickString(item.ownerUuid, item.owner_uuid) || null,
          }),
        )
      : Array.isArray(rawData?.my_high_priority_issues)
        ? rawData.my_high_priority_issues.map(
            (item: any): ProjectWorkbenchIssueItem => ({
              uuid: pickString(item.uuid, item.issueUuid, item.issue_uuid) || '',
              projectUuid: pickString(item.projectUuid, item.project_uuid) || '',
              title: pickString(item.title) || '未命名问题',
              issueLevel: normalizeProjectIssueLevel(
                pickString(item.issueLevel, item.issue_level),
              ),
              status: normalizeProjectIssueStatus(pickString(item.status)),
              isBlocked: pickBoolean(item.isBlocked, item.is_blocked) || false,
              plannedResolveAt:
                pickString(item.plannedResolveAt, item.planned_resolve_at) || null,
              ownerUuid: pickString(item.ownerUuid, item.owner_uuid) || null,
            }),
          )
        : [],
    pendingWeeklyReportProjects: Array.isArray(rawData?.pendingWeeklyReportProjects)
      ? rawData.pendingWeeklyReportProjects.map((item: any) => ({
          projectUuid: pickString(item.projectUuid, item.project_uuid, item.uuid) || '',
          projectName: pickString(item.projectName, item.project_name, item.name) || '未命名项目',
        }))
      : Array.isArray(rawData?.pending_weekly_report_projects)
        ? rawData.pending_weekly_report_projects.map((item: any) => ({
            projectUuid: pickString(item.projectUuid, item.project_uuid, item.uuid) || '',
            projectName: pickString(item.projectName, item.project_name, item.name) || '未命名项目',
          }))
        : [],
    pendingWorkHourProjects: Array.isArray(rawData?.pendingWorkHourProjects)
      ? rawData.pendingWorkHourProjects.map((item: any) => ({
          projectUuid: pickString(item.projectUuid, item.project_uuid, item.uuid) || '',
          projectName: pickString(item.projectName, item.project_name, item.name) || '未命名项目',
        }))
      : Array.isArray(rawData?.pending_work_hour_projects)
        ? rawData.pending_work_hour_projects.map((item: any) => ({
            projectUuid: pickString(item.projectUuid, item.project_uuid, item.uuid) || '',
            projectName: pickString(item.projectName, item.project_name, item.name) || '未命名项目',
          }))
        : [],
    todaySchedule: {
      date:
        pickString(rawTodaySchedule?.date) ||
        pickString(scheduleItems[0]?.startTime, scheduleItems[0]?.startDate) ||
        new Date().toISOString(),
      items: scheduleItems.map((item: any) => ({
        uuid:
          pickString(item.uuid, item.taskUuid, item.task_uuid, item.sourceUuid, item.source_uuid) ||
          '',
        title: pickString(item.title, item.name) || '未命名日程',
        startTime: pickString(item.startTime, item.start_date, item.startDate) || null,
        endTime: pickString(item.endTime, item.end_date, item.endDate) || null,
        projectUuid: pickString(item.projectUuid, item.project_uuid) || null,
        projectName: pickString(item.projectName, item.project_name) || null,
        sourceType: pickString(item.sourceType, item.source_type, item.status) || null,
        sourceUuid: pickString(item.sourceUuid, item.source_uuid, item.uuid) || null,
        description: pickString(item.description, item.content) || null,
      })),
      placeholderText:
        pickString(rawTodaySchedule?.placeholderText, rawTodaySchedule?.placeholder_text) ||
        '今日暂无已接入的日程数据',
    },
    recentActivities,
  };
};

const normalizeProjectDashboardData = (rawData: any): ProjectDashboardData => ({
  projectStats: {
    total: pickNumber(rawData?.projectStats?.total, rawData?.project_stats?.total) || 0,
    inProgress:
      pickNumber(
        rawData?.projectStats?.inProgress,
        rawData?.projectStats?.in_progress,
        rawData?.project_stats?.inProgress,
        rawData?.project_stats?.in_progress,
      ) || 0,
    overdue:
      pickNumber(rawData?.projectStats?.overdue, rawData?.project_stats?.overdue) || 0,
    highRisk:
      pickNumber(
        rawData?.projectStats?.highRisk,
        rawData?.projectStats?.high_risk,
        rawData?.project_stats?.highRisk,
        rawData?.project_stats?.high_risk,
      ) || 0,
    completed:
      pickNumber(rawData?.projectStats?.completed, rawData?.project_stats?.completed) || 0,
  },
  resourceStats: {
    totalResources:
      pickNumber(
        rawData?.resourceStats?.totalResources,
        rawData?.resourceStats?.total_resources,
        rawData?.resource_stats?.totalResources,
        rawData?.resource_stats?.total_resources,
      ) || 0,
    overloaded:
      pickNumber(
        rawData?.resourceStats?.overloaded,
        rawData?.resource_stats?.overloaded,
      ) || 0,
    idle: pickNumber(rawData?.resourceStats?.idle, rawData?.resource_stats?.idle) || 0,
    averageLoadPercent:
      pickNumber(
        rawData?.resourceStats?.averageLoadPercent,
        rawData?.resourceStats?.average_load_percent,
        rawData?.resource_stats?.averageLoadPercent,
        rawData?.resource_stats?.average_load_percent,
      ) || 0,
  },
  riskStats: {
    totalRisks:
      pickNumber(
        rawData?.riskStats?.totalRisks,
        rawData?.riskStats?.total_risks,
        rawData?.risk_stats?.totalRisks,
        rawData?.risk_stats?.total_risks,
      ) || 0,
    highRisks:
      pickNumber(
        rawData?.riskStats?.highRisks,
        rawData?.riskStats?.high_risks,
        rawData?.risk_stats?.highRisks,
        rawData?.risk_stats?.high_risks,
      ) || 0,
  },
  healthDistribution: {
    healthy:
      pickNumber(
        rawData?.healthDistribution?.healthy,
        rawData?.health_distribution?.healthy,
      ) || 0,
    attention:
      pickNumber(
        rawData?.healthDistribution?.attention,
        rawData?.health_distribution?.attention,
      ) || 0,
    risk:
      pickNumber(rawData?.healthDistribution?.risk, rawData?.health_distribution?.risk) || 0,
    critical:
      pickNumber(
        rawData?.healthDistribution?.critical,
        rawData?.health_distribution?.critical,
      ) || 0,
  },
  highRiskProjects: Array.isArray(rawData?.highRiskProjects)
    ? rawData.highRiskProjects.map((item: any) => ({
        uuid: pickString(item.uuid, item.projectUuid, item.project_uuid) || '',
        name: pickString(item.name, item.projectName, item.project_name) || '未命名项目',
        healthScore: pickNumber(item.healthScore, item.health_score) || 0,
        healthLevel: pickString(item.healthLevel, item.health_level) || 'healthy',
        status: pickString(item.status, item.projectStatus, item.project_status) || 'not_started',
        endDate:
          pickString(item.endDate, item.end_date, item.planEndDate, item.plan_end_date) || null,
        openRiskCount:
          pickNumber(item.openRiskCount, item.open_risk_count, item.riskCount, item.risk_count) || 0,
        highRiskCount:
          pickNumber(item.highRiskCount, item.high_risk_count) || 0,
      }))
    : Array.isArray(rawData?.high_risk_projects)
      ? rawData.high_risk_projects.map((item: any) => ({
          uuid: pickString(item.uuid, item.projectUuid, item.project_uuid) || '',
          name: pickString(item.name, item.projectName, item.project_name) || '未命名项目',
          healthScore: pickNumber(item.healthScore, item.health_score) || 0,
          healthLevel: pickString(item.healthLevel, item.health_level) || 'healthy',
          status: pickString(item.status, item.projectStatus, item.project_status) || 'not_started',
          endDate:
            pickString(item.endDate, item.end_date, item.planEndDate, item.plan_end_date) || null,
          openRiskCount:
            pickNumber(item.openRiskCount, item.open_risk_count, item.riskCount, item.risk_count) || 0,
          highRiskCount:
            pickNumber(item.highRiskCount, item.high_risk_count) || 0,
        }))
      : [],
  resourceRanking: Array.isArray(rawData?.resourceRanking)
    ? rawData.resourceRanking.map((item: any) => ({
        resourceUuid: pickString(item.resourceUuid, item.memberUuid, item.resource_uuid) || '',
        loadPercent: pickNumber(item.loadPercent, item.load_percent) || 0,
        loadStatus: pickString(item.loadStatus, item.load_status) || 'balanced',
        taskCount: pickNumber(item.taskCount, item.task_count) || 0,
        estimatedHours: pickNumber(item.estimatedHours, item.estimated_hours) || 0,
      }))
    : Array.isArray(rawData?.resource_ranking)
      ? rawData.resource_ranking.map((item: any) => ({
          resourceUuid: pickString(item.resourceUuid, item.memberUuid, item.resource_uuid) || '',
          loadPercent: pickNumber(item.loadPercent, item.load_percent) || 0,
          loadStatus: pickString(item.loadStatus, item.load_status) || 'balanced',
          taskCount: pickNumber(item.taskCount, item.task_count) || 0,
          estimatedHours: pickNumber(item.estimatedHours, item.estimated_hours) || 0,
        }))
      : [],
  aiRiskAlerts: Array.isArray(rawData?.aiRiskAlerts)
    ? rawData.aiRiskAlerts.map((item: any) => ({
        uuid: pickString(item.uuid) || '',
        projectUuid: pickString(item.projectUuid, item.project_uuid) || '',
        summary: pickString(item.summary, item.content) || '',
        analysisType: pickString(item.analysisType, item.analysis_type) || '',
        createdAt: pickString(item.createdAt, item.created_at) || '',
      }))
    : Array.isArray(rawData?.ai_risk_alerts)
      ? rawData.ai_risk_alerts.map((item: any) => ({
          uuid: pickString(item.uuid) || '',
          projectUuid: pickString(item.projectUuid, item.project_uuid) || '',
          summary: pickString(item.summary, item.content) || '',
          analysisType: pickString(item.analysisType, item.analysis_type) || '',
          createdAt: pickString(item.createdAt, item.created_at) || '',
        }))
      : [],
  recentActivities: Array.isArray(rawData?.recentActivities)
    ? rawData.recentActivities.map(normalizeProjectActivityItem)
    : Array.isArray(rawData?.recent_activities)
      ? rawData.recent_activities.map(normalizeProjectActivityItem)
      : [],
});

const normalizeKanbanColumns = (rawData: any): KanbanColumn[] => {
  const rawColumns = Array.isArray(rawData)
    ? rawData
    : Array.isArray(rawData?.columns)
      ? rawData.columns
      : Array.isArray(rawData?.items)
        ? rawData.items
        : [];

  if (!rawColumns.length && Array.isArray(rawData?.tasks)) {
    const grouped = new Map<ProjectTaskItem['status'], ProjectTaskItem[]>();
    rawData.tasks.map(normalizeProjectTaskItem).forEach((task: ProjectTaskItem) => {
      const list = grouped.get(task.status) || [];
      list.push(task);
      grouped.set(task.status, list);
    });
    return taskStatusFallbackOrder.map(item => ({
      key: item.key,
      name: item.name,
      tasks: grouped.get(item.key) || [],
      total: (grouped.get(item.key) || []).length,
    }));
  }

  return rawColumns.map((column: any) => {
    const key = normalizeTaskStatus(column.key ?? column.status ?? column.columnKey);
    const tasks = Array.isArray(column.tasks)
      ? column.tasks.map(normalizeProjectTaskItem)
      : Array.isArray(column.items)
        ? column.items.map(normalizeProjectTaskItem)
        : [];
    return {
      key,
      name: pickString(column.name, column.title, column.columnName, column.statusName) ||
        taskStatusFallbackOrder.find(item => item.key === key)?.name ||
        key,
      tasks,
      total: pickNumber(column.total, column.count) || tasks.length,
    };
  });
};

const normalizeGlobalKanbanColumns = (rawData: any): GlobalKanbanColumn[] => {
  const rawColumns = Array.isArray(rawData)
    ? rawData
    : Array.isArray(rawData?.columns)
      ? rawData.columns
      : Array.isArray(rawData?.items)
        ? rawData.items
        : [];

  if (!rawColumns.length && Array.isArray(rawData?.tasks)) {
    const grouped = new Map<ProjectTaskItem['status'], GlobalKanbanTaskItem[]>();
    rawData.tasks.map(normalizeGlobalKanbanTaskItem).forEach((task: GlobalKanbanTaskItem) => {
      const list = grouped.get(task.status) || [];
      list.push(task);
      grouped.set(task.status, list);
    });

    return taskStatusFallbackOrder.map(item => ({
      key: item.key,
      name: item.name,
      tasks: grouped.get(item.key) || [],
      total: (grouped.get(item.key) || []).length,
    }));
  }

  return rawColumns.map((column: any) => {
    const key = normalizeTaskStatus(column.key ?? column.status ?? column.columnKey);
    const tasks = Array.isArray(column.tasks)
      ? column.tasks.map(normalizeGlobalKanbanTaskItem)
      : Array.isArray(column.items)
        ? column.items.map(normalizeGlobalKanbanTaskItem)
        : [];
    return {
      key,
      name:
        pickString(column.name, column.title, column.columnName, column.statusName) ||
        taskStatusFallbackOrder.find(item => item.key === key)?.name ||
        key,
      tasks,
      total: pickNumber(column.total, column.count) || tasks.length,
    };
  });
};

const normalizeGanttData = (rawData: any): ProjectGanttData => {
  const rawTasks = Array.isArray(rawData?.tasks) ? rawData.tasks : [];
  const rawDependencies = Array.isArray(rawData?.dependencies) ? rawData.dependencies : [];

  return {
    tasks: rawTasks.map((rawTask: any) => {
      const task = normalizeProjectTaskItem(rawTask);
      return {
        id: task.uuid,
        uuid: task.uuid,
        taskUuid: task.uuid,
        title: task.title,
        startDate: task.startDate,
        endDate: task.endDate,
        progress: pickNumber(rawTask.progress, rawTask.progressPercent, rawTask.percent) || 0,
        assigneeUuid: task.assigneeUuid,
        status: task.status,
        parentTaskUuid: task.parentTaskUuid,
        isKeyTask: task.isKeyTask,
        stageUuid: task.stageUuid,
        stageName: task.stageName,
        isDelayed: task.isDelayed,
        delayReason: task.delayReason,
        isOverdue: task.isOverdue,
        baselineStartDate:
          pickString(rawTask.baselineStartDate, rawTask.baseline_start_date) || null,
        baselineEndDate: pickString(rawTask.baselineEndDate, rawTask.baseline_end_date) || null,
        resourceSummary: pickString(rawTask.resourceSummary, rawTask.resource_summary) || null,
        riskHints: Array.isArray(rawTask.riskHints)
          ? rawTask.riskHints.filter((item: unknown): item is string => typeof item === 'string')
          : Array.isArray(rawTask.risk_hints)
            ? rawTask.risk_hints.filter((item: unknown): item is string => typeof item === 'string')
            : null,
      };
    }),
    dependencies: rawDependencies.map((dependency: any) => ({
      id:
        pickString(dependency.id, dependency.uuid, dependency.dependencyUuid, dependency.dependency_uuid) ||
        '',
      sourceTaskId:
        pickString(
          dependency.sourceTaskId,
          dependency.sourceTaskUuid,
          dependency.predecessorTaskUuid,
          dependency.predecessor_task_uuid,
        ) || '',
      targetTaskId:
        pickString(
          dependency.targetTaskId,
          dependency.targetTaskUuid,
          dependency.successorTaskUuid,
          dependency.successor_task_uuid,
        ) || '',
      type: 'finish_to_start',
      lagDays: pickNumber(dependency.lagDays, dependency.lag_days) || 0,
    })),
  };
};

const normalizeGlobalGanttData = (rawData: any): GlobalGanttData => {
  const base = normalizeGanttData(rawData);

  return {
    ...base,
    tasks: (Array.isArray(rawData?.tasks) ? rawData.tasks : []).map((rawTask: any) => {
      const task = normalizeProjectTaskItem(rawTask);
      const baseTask = base.tasks.find(item => item.taskUuid === task.uuid || item.id === task.uuid);
      return {
        ...(baseTask || {
          id: task.uuid,
          uuid: task.uuid,
          taskUuid: task.uuid,
          title: task.title,
          startDate: task.startDate,
          endDate: task.endDate,
          progress: task.progress ?? 0,
          assigneeUuid: task.assigneeUuid,
          status: task.status,
          parentTaskUuid: task.parentTaskUuid,
          isKeyTask: task.isKeyTask,
          isOverdue: task.isOverdue,
          baselineStartDate: task.baselineStartDate,
          baselineEndDate: task.baselineEndDate,
          resourceSummary: null,
          riskHints: null,
        }),
        projectUuid: pickString(rawTask.projectUuid, rawTask.project_uuid) || task.projectUuid || '',
        projectName: pickString(rawTask.projectName, rawTask.project_name) || null,
        isCriticalPath: pickBoolean(rawTask.isCriticalPath, rawTask.is_critical_path) || false,
        isBlocked: pickBoolean(rawTask.isBlocked, rawTask.is_blocked) || false,
        riskLevel: normalizeGlobalTaskRiskLevel(pickString(rawTask.riskLevel, rawTask.risk_level)),
        loadPercent: pickNumber(rawTask.loadPercent, rawTask.load_percent) ?? null,
      } satisfies GlobalGanttTaskItem;
    }),
  };
};

const normalizeProjectResources = (rawData: any): ProjectResourceItem[] => {
  const rawItems = Array.isArray(rawData)
    ? rawData
    : Array.isArray(rawData?.items)
      ? rawData.items
      : Array.isArray(rawData?.members)
        ? rawData.members
        : [];

  return rawItems.map((item: any) => {
    const assignments = Array.isArray(item.assignments)
      ? item.assignments
      : Array.isArray(item.tasks)
        ? item.tasks
        : [];
    return {
      resourceUuid: pickString(item.resourceUuid, item.memberUuid, item.resource_uuid, item.member_uuid) || '',
      resourceName: pickString(item.resourceName, item.memberName, item.resource_name, item.member_name) || null,
      resourceType: 'member',
      taskCount: pickNumber(item.taskCount, item.currentTaskCount, item.task_count) || assignments.length,
      estimatedHours: pickNumber(item.estimatedHours, item.totalEstimatedHours, item.estimated_hours) || 0,
      availableHours: pickNumber(item.availableHours, item.capacityHours, item.available_hours) || 0,
      loadPercent: pickNumber(item.loadPercent, item.allocationPercent, item.load_percent) || 0,
      loadStatus:
        (pickString(item.loadStatus, item.riskStatus, item.load_status) as ProjectResourceItem['loadStatus']) ||
        'normal',
      assignments: assignments.map((assignment: any, index: number) => ({
        uuid:
          pickString(
            assignment.uuid,
            assignment.assignmentUuid,
            assignment.assignment_uuid,
            assignment.taskUuid,
            assignment.task_uuid,
          ) || `${pickString(item.resourceUuid, item.memberUuid) || 'resource'}-${index}`,
        taskUuid:
          pickString(assignment.taskUuid, assignment.task_uuid, assignment.uuid, assignment.id) || null,
        taskTitle: pickString(assignment.taskTitle, assignment.title, assignment.name) || null,
        resourceType: 'member',
        resourceUuid:
          pickString(
            assignment.resourceUuid,
            assignment.memberUuid,
            item.resourceUuid,
            item.memberUuid,
          ) || '',
        resourceName:
          pickString(
            assignment.resourceName,
            assignment.memberName,
            item.resourceName,
            item.memberName,
          ) || null,
        allocationPercent:
          pickNumber(assignment.allocationPercent, assignment.loadPercent, assignment.allocation_percent) ||
          0,
        estimatedHours:
          pickNumber(assignment.estimatedHours, assignment.estimated_hours, assignment.workHours) ?? null,
        actualHours:
          pickNumber(
            assignment.actualHours,
            assignment.actual_hours,
            assignment.loggedHours,
            assignment.logged_hours,
          ) ?? null,
        actualHoursSourceStatus: normalizeActualHoursSourceStatus(
          pickString(
            assignment.actualHoursSourceStatus,
            assignment.actual_hours_source_status,
            assignment.hoursSourceStatus,
            assignment.hours_source_status,
          ),
        ),
        startDate: pickString(assignment.startDate, assignment.start_date) || null,
        endDate: pickString(assignment.endDate, assignment.end_date, assignment.dueDate) || null,
      })),
    };
  });
};

const normalizePageResult = <T>(
  rawData: any,
  mapItem: (item: any) => T,
): PageResult<T> => ({
  items: Array.isArray(rawData?.items)
    ? rawData.items.map(mapItem)
    : Array.isArray(rawData?.list)
      ? rawData.list.map(mapItem)
      : Array.isArray(rawData?.records)
        ? rawData.records.map(mapItem)
        : [],
  total: pickNumber(rawData?.total, rawData?.summary?.total, rawData?.count) || 0,
  page: pickNumber(rawData?.page, rawData?.current, rawData?.pageNo, rawData?.page_num) || 1,
  pageSize:
    pickNumber(rawData?.pageSize, rawData?.page_size, rawData?.size, rawData?.limit) || 20,
});

const toQueryParams = <T extends object>(params?: T) =>
  (params ? ({ ...params } as Record<string, unknown>) : undefined);

const normalizeResourceCenterListItem = (rawItem: any): ResourceCenterListItem => ({
  memberUuid: pickString(rawItem.memberUuid, rawItem.member_uuid) || '',
  memberName: pickString(rawItem.memberName, rawItem.member_name) || '',
  departmentUuid: pickString(rawItem.departmentUuid, rawItem.department_uuid) || undefined,
  departmentName: pickString(rawItem.departmentName, rawItem.department_name) || undefined,
  projectCount: pickNumber(rawItem.projectCount, rawItem.project_count) || 0,
  currentTaskCount: pickNumber(rawItem.currentTaskCount, rawItem.current_task_count) || 0,
  estimatedHours: pickNumber(rawItem.estimatedHours, rawItem.estimated_hours) || 0,
  availableHours: pickNumber(rawItem.availableHours, rawItem.available_hours) || 0,
  loadPercent: pickNumber(rawItem.loadPercent, rawItem.load_percent) || 0,
  loadStatus:
    (pickString(rawItem.loadStatus, rawItem.load_status) as ResourceCenterListItem['loadStatus']) ||
    'normal',
  riskStatus:
    (pickString(rawItem.riskStatus, rawItem.risk_status) as ResourceCenterRiskStatus) || 'normal',
  conflictTaskCount: pickNumber(rawItem.conflictTaskCount, rawItem.conflict_task_count) || 0,
  conflictRiskCount: pickNumber(rawItem.conflictRiskCount, rawItem.conflict_risk_count) || 0,
  loadHint: pickString(rawItem.loadHint, rawItem.load_hint) || null,
});

const normalizeResourceCenterPageData = (rawData: any): ResourceCenterPageData => ({
  items: Array.isArray(rawData?.items)
    ? rawData.items.map(normalizeResourceCenterListItem)
    : Array.isArray(rawData?.list)
      ? rawData.list.map(normalizeResourceCenterListItem)
      : Array.isArray(rawData?.records)
        ? rawData.records.map(normalizeResourceCenterListItem)
        : [],
  total: pickNumber(rawData?.total, rawData?.summary?.total, rawData?.count) || 0,
  page: pickNumber(rawData?.page, rawData?.current, rawData?.pageNo, rawData?.page_num) || 1,
  pageSize:
    pickNumber(rawData?.pageSize, rawData?.page_size, rawData?.size, rawData?.limit) || 20,
  summary: {
    totalMembers:
      pickNumber(
        rawData?.summary?.totalMembers,
        rawData?.summary?.total_members,
        rawData?.totalMembers,
        rawData?.total_members,
      ) || 0,
    overloadedMembers:
      pickNumber(
        rawData?.summary?.overloadedMembers,
        rawData?.summary?.overloaded_members,
        rawData?.overloadedMembers,
        rawData?.overloaded_members,
      ) || 0,
    highRiskMembers:
      pickNumber(
        rawData?.summary?.highRiskMembers,
        rawData?.summary?.high_risk_members,
        rawData?.highRiskMembers,
        rawData?.high_risk_members,
      ) || 0,
    averageLoadPercent:
      pickNumber(
        rawData?.summary?.averageLoadPercent,
        rawData?.summary?.average_load_percent,
        rawData?.averageLoadPercent,
        rawData?.average_load_percent,
      ) || 0,
    workHourSummary: {
      totalRecordedHours:
        pickNumber(
          rawData?.summary?.workHourSummary?.totalRecordedHours,
          rawData?.summary?.workHourSummary?.total_recorded_hours,
          rawData?.summary?.work_hour_summary?.totalRecordedHours,
          rawData?.summary?.work_hour_summary?.total_recorded_hours,
        ) || 0,
      recordCount:
        pickNumber(
          rawData?.summary?.workHourSummary?.recordCount,
          rawData?.summary?.workHourSummary?.record_count,
          rawData?.summary?.work_hour_summary?.recordCount,
          rawData?.summary?.work_hour_summary?.record_count,
        ) || 0,
      memberCount:
        pickNumber(
          rawData?.summary?.workHourSummary?.memberCount,
          rawData?.summary?.workHourSummary?.member_count,
          rawData?.summary?.work_hour_summary?.memberCount,
          rawData?.summary?.work_hour_summary?.member_count,
        ) || 0,
      projectCount:
        pickNumber(
          rawData?.summary?.workHourSummary?.projectCount,
          rawData?.summary?.workHourSummary?.project_count,
          rawData?.summary?.work_hour_summary?.projectCount,
          rawData?.summary?.work_hour_summary?.project_count,
        ) || 0,
      latestWorkDate:
        pickString(
          rawData?.summary?.workHourSummary?.latestWorkDate,
          rawData?.summary?.workHourSummary?.latest_work_date,
          rawData?.summary?.work_hour_summary?.latestWorkDate,
          rawData?.summary?.work_hour_summary?.latest_work_date,
        ) || null,
      memberSummaries: Array.isArray(rawData?.summary?.workHourSummary?.memberSummaries)
        ? rawData.summary.workHourSummary.memberSummaries.map((item: any) => ({
            memberUuid: pickString(item.memberUuid, item.member_uuid) || '',
            memberName: pickString(item.memberName, item.member_name) || '',
            departmentName:
              pickString(item.departmentName, item.department_name) || undefined,
            projectCount: pickNumber(item.projectCount, item.project_count) || 0,
            recordCount: pickNumber(item.recordCount, item.record_count) || 0,
            totalHours: pickNumber(item.totalHours, item.total_hours) || 0,
            latestWorkDate:
              pickString(item.latestWorkDate, item.latest_work_date) || null,
          }))
        : Array.isArray(rawData?.summary?.work_hour_summary?.memberSummaries)
          ? rawData.summary.work_hour_summary.memberSummaries.map((item: any) => ({
              memberUuid: pickString(item.memberUuid, item.member_uuid) || '',
              memberName: pickString(item.memberName, item.member_name) || '',
              departmentName:
                pickString(item.departmentName, item.department_name) || undefined,
              projectCount: pickNumber(item.projectCount, item.project_count) || 0,
              recordCount: pickNumber(item.recordCount, item.record_count) || 0,
              totalHours: pickNumber(item.totalHours, item.total_hours) || 0,
              latestWorkDate:
                pickString(item.latestWorkDate, item.latest_work_date) || null,
            }))
          : [],
      projectSummaries: Array.isArray(rawData?.summary?.workHourSummary?.projectSummaries)
        ? rawData.summary.workHourSummary.projectSummaries.map((item: any) => ({
            projectUuid: pickString(item.projectUuid, item.project_uuid) || '',
            projectName: pickString(item.projectName, item.project_name) || '',
            memberCount: pickNumber(item.memberCount, item.member_count) || 0,
            recordCount: pickNumber(item.recordCount, item.record_count) || 0,
            totalHours: pickNumber(item.totalHours, item.total_hours) || 0,
            latestWorkDate:
              pickString(item.latestWorkDate, item.latest_work_date) || null,
          }))
        : Array.isArray(rawData?.summary?.work_hour_summary?.projectSummaries)
          ? rawData.summary.work_hour_summary.projectSummaries.map((item: any) => ({
              projectUuid: pickString(item.projectUuid, item.project_uuid) || '',
              projectName: pickString(item.projectName, item.project_name) || '',
              memberCount: pickNumber(item.memberCount, item.member_count) || 0,
              recordCount: pickNumber(item.recordCount, item.record_count) || 0,
              totalHours: pickNumber(item.totalHours, item.total_hours) || 0,
              latestWorkDate:
                pickString(item.latestWorkDate, item.latest_work_date) || null,
            }))
          : [],
    },
  },
});

export async function createProject(
  body: CreateProjectPayload,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectItem>>('/bff/project-center/projects', {
    method: 'POST',
    data: body,
    ...(options || {}),
  });
  return result.data;
}

export async function queryProjectTemplates(
  params?: QueryProjectTemplatesParams,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<PageResult<ProjectTemplateItem>>>(
    '/bff/project-center/templates',
    {
      method: 'GET',
      params: toQueryParams(params),
      ...(options || {}),
    },
  );
  return result.data;
}

export async function queryMyTasks(
  params?: QueryMyTasksParams,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<PageResult<MyTaskItem>>>('/bff/project-center/my/tasks', {
    method: 'GET',
    params: toQueryParams(params),
    ...(options || {}),
  });
  return result.data;
}

export async function getProjectTemplateDetail(
  uuid: string,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectTemplateItem>>(
    `/bff/project-center/templates/${uuid}`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );
  return result.data;
}

export async function createProjectTemplate(
  body: CreateProjectTemplatePayload,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectTemplateItem>>(
    '/bff/project-center/templates',
    {
      method: 'POST',
      data: body,
      ...(options || {}),
    },
  );
  return result.data;
}

export async function updateProjectTemplate(
  uuid: string,
  body: UpdateProjectTemplatePayload,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectTemplateItem>>(
    `/bff/project-center/templates/${uuid}`,
    {
      method: 'PATCH',
      data: body,
      ...(options || {}),
    },
  );
  return result.data;
}

export async function updateProjectTemplateStatus(
  uuid: string,
  body: { isActive: boolean },
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectTemplateItem>>(
    `/bff/project-center/templates/${uuid}/status`,
    {
      method: 'PATCH',
      data: body,
      ...(options || {}),
    },
  );
  return result.data;
}

export async function duplicateProjectTemplate(
  uuid: string,
  body?: DuplicateProjectTemplatePayload,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectTemplateItem>>(
    `/bff/project-center/templates/${uuid}/duplicate`,
    {
      method: 'POST',
      data: body,
      ...(options || {}),
    },
  );
  return result.data;
}

export async function createProjectFromTemplate(
  uuid: string,
  body: CreateProjectFromTemplatePayload,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectItem>>(
    `/bff/project-center/templates/${uuid}/create-project`,
    {
      method: 'POST',
      data: body,
      ...(options || {}),
    },
  );
  return result.data;
}

export async function saveProjectAsTemplate(
  uuid: string,
  body: SaveProjectAsTemplatePayload,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectTemplateItem>>(
    `/bff/project-center/projects/${uuid}/save-as-template`,
    {
      method: 'POST',
      data: body,
      ...(options || {}),
    },
  );
  return result.data;
}

export async function listProjectSubscriptions(
  params?: { memberUuid?: string; subjectType?: ProjectSubscriptionSubjectType; subjectUuid?: string },
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<{ items: Array<{ uuid: string; memberUuid: string; subjectType: ProjectSubscriptionSubjectType; subjectUuid: string }>; total: number; page: number; pageSize: number }>>(
    '/bff/project-center/subscriptions',
    {
      method: 'GET',
      params: toQueryParams(params),
      ...(options || {}),
    },
  );
  return result.data;
}

export async function toggleProjectSubscription(
  body: { subjectType: ProjectSubscriptionSubjectType; subjectUuid: string },
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<{ subscribed: boolean; subscription?: { uuid: string; memberUuid: string; subjectType: ProjectSubscriptionSubjectType; subjectUuid: string } | null }>>(
    '/bff/project-center/subscriptions/toggle',
    {
      method: 'POST',
      data: body,
      ...(options || {}),
    },
  );
  return result.data;
}

export async function queryProjects(
  params?: QueryProjectsParams,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<PageResult<ProjectItem>>>('/bff/project-center/projects', {
    method: 'GET',
    params: toQueryParams(params),
    ...(options || {}),
  });
  return normalizePageResult(result.data, normalizeProjectItem);
}

export async function getProjectDetail(uuid: string, options?: ApiRequestOptions) {
  const result = await request<ApiResponse<ProjectItem>>(`/bff/project-center/projects/${uuid}`, {
    method: 'GET',
    ...(options || {}),
  });
  return normalizeProjectItem(result.data);
}

export async function updateProject(
  uuid: string,
  body: UpdateProjectPayload,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectItem>>(`/bff/project-center/projects/${uuid}`, {
    method: 'PATCH',
    data: body,
    ...(options || {}),
  });
  return result.data;
}

export async function confirmProjectStatus(
  uuid: string,
  body: ConfirmProjectStatusPayload,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectItem>>(
    `/bff/project-center/projects/${uuid}/status-confirmation`,
    {
      method: 'PATCH',
      data: body,
      ...(options || {}),
    },
  );
  return normalizeProjectItem(result.data);
}

export async function archiveProject(uuid: string, options?: ApiRequestOptions) {
  const result = await request<ApiResponse<ProjectItem>>(`/bff/project-center/projects/${uuid}`, {
    method: 'DELETE',
    ...(options || {}),
  });
  return result.data;
}

export async function queryProjectActivities(
  uuid: string,
  params?: { page?: number; pageSize?: number },
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<PageResult<ProjectActivityItem>>>(
    `/bff/project-center/projects/${uuid}/activities`,
    {
      method: 'GET',
      params,
      ...(options || {}),
    },
  );
  return {
    ...result.data,
    items: Array.isArray(result.data?.items)
      ? result.data.items.map(normalizeProjectActivityItem)
      : [],
  };
}

export async function queryProjectIssues(
  projectUuid: string,
  params?: QueryProjectIssuesParams,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectIssuePageData | Record<string, unknown>>>(
    `/bff/project-center/projects/${projectUuid}/issues`,
    {
      method: 'GET',
      params: toQueryParams(params),
      ...(options || {}),
    },
  );

  return {
    ...normalizePageResult(result.data, normalizeProjectIssueItem),
    summary: normalizeProjectIssueSummary((result.data as any)?.summary),
  } satisfies ProjectIssuePageData;
}

export async function getProjectIssueDetail(
  projectUuid: string,
  issueUuid: string,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectIssueItem | Record<string, unknown>>>(
    `/bff/project-center/projects/${projectUuid}/issues/${issueUuid}`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );
  return normalizeProjectIssueItem(result.data);
}

export async function createProjectIssue(
  projectUuid: string,
  body: CreateProjectIssuePayload,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectIssueItem | Record<string, unknown>>>(
    `/bff/project-center/projects/${projectUuid}/issues`,
    {
      method: 'POST',
      data: body,
      ...(options || {}),
    },
  );
  return normalizeProjectIssueItem(result.data);
}

export async function updateProjectIssue(
  projectUuid: string,
  issueUuid: string,
  body: UpdateProjectIssuePayload,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectIssueItem | Record<string, unknown>>>(
    `/bff/project-center/projects/${projectUuid}/issues/${issueUuid}`,
    {
      method: 'PATCH',
      data: body,
      ...(options || {}),
    },
  );
  return normalizeProjectIssueItem(result.data);
}

export async function updateProjectIssueStatus(
  projectUuid: string,
  issueUuid: string,
  body: UpdateProjectIssueStatusPayload,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectIssueItem | Record<string, unknown>>>(
    `/bff/project-center/projects/${projectUuid}/issues/${issueUuid}/status`,
    {
      method: 'PATCH',
      data: body,
      ...(options || {}),
    },
  );
  return normalizeProjectIssueItem(result.data);
}

export async function updateProjectIssueBlocked(
  projectUuid: string,
  issueUuid: string,
  body: UpdateProjectIssueBlockedPayload,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectIssueItem | Record<string, unknown>>>(
    `/bff/project-center/projects/${projectUuid}/issues/${issueUuid}/blocked`,
    {
      method: 'PATCH',
      data: body,
      ...(options || {}),
    },
  );
  return normalizeProjectIssueItem(result.data);
}

export async function updateProjectIssueTasks(
  projectUuid: string,
  issueUuid: string,
  body: UpdateProjectIssueTasksPayload,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectIssueItem | Record<string, unknown>>>(
    `/bff/project-center/projects/${projectUuid}/issues/${issueUuid}/tasks`,
    {
      method: 'PUT',
      data: body,
      ...(options || {}),
    },
  );
  return normalizeProjectIssueItem(result.data);
}

export async function createProjectTask(
  projectUuid: string,
  body: CreateProjectTaskPayload,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectTaskItem>>(
    `/bff/project-center/projects/${projectUuid}/tasks`,
    {
      method: 'POST',
      data: body,
      ...(options || {}),
    },
  );
  return result.data;
}

export async function queryProjectTasks(
  projectUuid: string,
  params?: { status?: string; assigneeUuid?: string; keyword?: string; view?: string },
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectTaskItem[]>>(
    `/bff/project-center/projects/${projectUuid}/tasks`,
    {
      method: 'GET',
      params,
      ...(options || {}),
    },
  );
  return Array.isArray(result.data) ? result.data.map(normalizeProjectTaskItem) : [];
}

export async function getProjectTaskDetail(
  projectUuid: string,
  taskUuid: string,
  options?: ApiRequestOptions,
) {
  try {
    const result = await request<ApiResponse<ProjectTaskItem>>(
      `/bff/project-center/projects/${projectUuid}/tasks/${taskUuid}`,
      {
        method: 'GET',
        ...(options || {}),
      },
    );
    return normalizeProjectTaskItem(result.data);
  } catch (error) {
    const list = await queryProjectTasks(projectUuid, undefined, options);
    const task = list.find(item => (item.taskUuid || item.uuid) === taskUuid);
    if (!task) {
      throw error;
    }
    return task;
  }
}

export async function updateProjectTask(
  projectUuid: string,
  taskUuid: string,
  body: UpdateProjectTaskPayload,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectTaskItem>>(
    `/bff/project-center/projects/${projectUuid}/tasks/${taskUuid}`,
    {
      method: 'PATCH',
      data: body,
      ...(options || {}),
    },
  );
  return result.data;
}

export async function deleteProjectTask(
  projectUuid: string,
  taskUuid: string,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<boolean>>(
    `/bff/project-center/projects/${projectUuid}/tasks/${taskUuid}`,
    {
      method: 'DELETE',
      ...(options || {}),
    },
  );
  return result.data;
}

export async function queryProjectKanban(
  projectUuid: string,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<KanbanColumn[] | { columns?: KanbanColumn[]; tasks?: any[] }>>(
    `/bff/project-center/projects/${projectUuid}/kanban`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );
  return normalizeKanbanColumns(result.data);
}

export async function queryGlobalKanban(
  params?: QueryGlobalKanbanParams,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<GlobalKanbanData | Record<string, unknown>>>(
    '/bff/project-center/boards',
    {
      method: 'GET',
      params: toQueryParams(params),
      ...(options || {}),
    },
  );
  const columns = normalizeGlobalKanbanColumns(result.data);
  const tasks = columns.flatMap((column) => column.tasks);

  return {
    columns,
    summary: {
      overdueCount:
        pickNumber(
          (result.data as any)?.summary?.overdueCount,
          (result.data as any)?.summary?.overdue_count,
        ) || tasks.filter((task) => Boolean(task.isOverdue)).length,
      blockedCount:
        pickNumber(
          (result.data as any)?.summary?.blockedCount,
          (result.data as any)?.summary?.blocked_count,
        ) || tasks.filter((task) => Boolean(task.isBlocked)).length,
      highRiskCount:
        pickNumber(
          (result.data as any)?.summary?.highRiskCount,
          (result.data as any)?.summary?.high_risk_count,
        ) || tasks.filter((task) => ['high', 'critical'].includes(task.riskLevel || '')).length,
    },
  } satisfies GlobalKanbanData;
}

export async function moveProjectTaskInKanban(
  projectUuid: string,
  taskUuid: string,
  body: MoveProjectTaskInKanbanPayload,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectTaskItem>>(
    `/bff/project-center/projects/${projectUuid}/kanban/tasks/${taskUuid}/move`,
    {
      method: 'PATCH',
      data: body,
      ...(options || {}),
    },
  );
  return result.data;
}

export async function moveGlobalTaskInKanban(
  taskUuid: string,
  body: MoveGlobalTaskInKanbanPayload,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectTaskItem>>(
    `/bff/project-center/boards/tasks/${taskUuid}/move`,
    {
      method: 'PATCH',
      data: body,
      ...(options || {}),
    },
  );
  return result.data;
}

export async function queryProjectGantt(
  projectUuid: string,
  params?: { status?: string },
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectGanttData | Record<string, unknown>>>(
    `/bff/project-center/projects/${projectUuid}/gantt`,
    {
      method: 'GET',
      params: toQueryParams(params),
      ...(options || {}),
    },
  );
  return normalizeGanttData(result.data);
}

export async function queryGlobalGantt(
  params?: QueryGlobalGanttParams,
  options?: ApiRequestOptions,
) {
  const { viewMode: _viewMode, ...queryParams } = params || {};
  const result = await request<ApiResponse<GlobalGanttData | Record<string, unknown>>>(
    '/bff/project-center/gantt',
    {
      method: 'GET',
      params: toQueryParams(queryParams),
      ...(options || {}),
    },
  );
  return normalizeGlobalGanttData(result.data);
}

export async function createProjectGanttDependency(
  projectUuid: string,
  body: CreateProjectTaskDependencyPayload,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<GanttDependencyItem>>(
    `/bff/project-center/projects/${projectUuid}/gantt/dependencies`,
    {
      method: 'POST',
      data: body,
      ...(options || {}),
    },
  );
  return result.data;
}

export async function updateProjectTaskSchedule(
  projectUuid: string,
  taskUuid: string,
  body: { startDate?: string; endDate?: string },
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectTaskItem>>(
    `/bff/project-center/projects/${projectUuid}/gantt/tasks/${taskUuid}/schedule`,
    {
      method: 'PATCH',
      data: body,
      ...(options || {}),
    },
  );
  return result.data;
}

export async function deleteProjectGanttDependency(
  projectUuid: string,
  dependencyUuid: string,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<boolean>>(
    `/bff/project-center/projects/${projectUuid}/gantt/dependencies/${dependencyUuid}`,
    {
      method: 'DELETE',
      ...(options || {}),
    },
  );
  return result.data;
}

export async function queryProjectResources(
  projectUuid: string,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectResourceItem[] | { items?: ProjectResourceItem[]; members?: any[] }>>(
    `/bff/project-center/projects/${projectUuid}/resource-load`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );
  return normalizeProjectResources(result.data);
}

export async function queryResourceCenter(
  params?: ResourceCenterQueryParams,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ResourceCenterPageData>>(
    '/bff/project-center/resources',
    {
      method: 'GET',
      params: toQueryParams(params),
      ...(options || {}),
    },
  );
  return normalizeResourceCenterPageData(result.data);
}

export async function getResourceCenterMemberDetail(
  memberUuid: string,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ResourceCenterMemberDetail>>(
    `/bff/project-center/resources/${memberUuid}`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );
  const rawData = result.data as any;
  return {
    ...normalizeResourceCenterListItem(rawData),
    projects: Array.isArray(rawData?.projects)
      ? rawData.projects.map((item: any) => ({
          uuid: pickString(item.uuid, item.projectUuid, item.project_uuid) || '',
          name: pickString(item.name, item.projectName, item.project_name) || '',
          status: pickString(item.status) || 'not_started',
          healthLevel: pickString(item.healthLevel, item.health_level) || 'healthy',
        }))
      : [],
    tasks: Array.isArray(rawData?.tasks)
      ? rawData.tasks.map((item: any) => ({
          uuid: pickString(item.uuid) || '',
          projectUuid: pickString(item.projectUuid, item.project_uuid) || '',
          projectName: pickString(item.projectName, item.project_name) || '',
          title: pickString(item.title) || '未命名任务',
          status: normalizeTaskStatus(pickString(item.status)),
          priority: normalizeTaskPriority(pickString(item.priority)),
          endDate: pickString(item.endDate, item.end_date) || null,
          estimatedHours: pickNumber(item.estimatedHours, item.estimated_hours) ?? null,
        }))
      : [],
    risks: Array.isArray(rawData?.risks)
      ? rawData.risks.map(normalizeProjectRiskItem)
      : [],
    conflictSummary:
      rawData?.conflictSummary && typeof rawData.conflictSummary === 'object'
        ? {
            conflictCount:
              pickNumber(
                rawData.conflictSummary.conflictCount,
                rawData.conflictSummary.conflict_count,
              ) || 0,
            conflictTasks: Array.isArray(rawData.conflictSummary.conflictTasks)
              ? rawData.conflictSummary.conflictTasks.map((item: any) => ({
                  uuid: pickString(item.uuid) || '',
                  title: pickString(item.title) || '未命名任务',
                  projectUuid: pickString(item.projectUuid, item.project_uuid) || '',
                  projectName: pickString(item.projectName, item.project_name) || '',
                  endDate: pickString(item.endDate, item.end_date) || null,
                }))
              : [],
            conflictRisks: Array.isArray(rawData.conflictSummary.conflictRisks)
              ? rawData.conflictSummary.conflictRisks.map((item: any) => ({
                  uuid: pickString(item.uuid) || '',
                  title: pickString(item.title) || '未命名风险',
                  riskLevel: normalizeProjectRiskLevel(
                    pickString(item.riskLevel, item.risk_level),
                  ),
                  description: pickString(item.description) || '',
                }))
              : [],
          }
        : null,
  };
}

export async function createProjectResourceAssignment(
  projectUuid: string,
  body: CreateProjectResourceAssignmentPayload,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<unknown>>(
    `/bff/project-center/projects/${projectUuid}/resources/assignments`,
    {
      method: 'POST',
      data: body,
      ...(options || {}),
    },
  );
  return result.data;
}

export async function deleteProjectResourceAssignment(
  projectUuid: string,
  assignmentUuid: string,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<boolean>>(
    `/bff/project-center/projects/${projectUuid}/resources/assignments/${assignmentUuid}`,
    {
      method: 'DELETE',
      ...(options || {}),
    },
  );
  return result.data;
}

export async function queryProjectRisks(
  projectUuid: string,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectRiskItem[]>>(
    `/bff/project-center/projects/${projectUuid}/risks`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );
  return Array.isArray(result.data) ? result.data.map(normalizeProjectRiskItem) : [];
}

export async function queryGlobalProjectRisks(options?: ApiRequestOptions) {
  const result = await request<ApiResponse<GlobalProjectRiskItem[]>>(
    '/bff/project-center/risks',
    {
      method: 'GET',
      ...(options || {}),
    },
  );
  return Array.isArray(result.data)
    ? result.data.map((item: any) => ({
        ...normalizeProjectRiskItem(item),
        project: {
          projectUuid:
            pickString(item.project?.projectUuid, item.project?.project_uuid, item.projectUuid) || '',
          projectName:
            pickString(item.project?.projectName, item.project?.project_name) || '未命名项目',
          projectStatus:
            (pickString(item.project?.projectStatus, item.project?.project_status) as any) ||
            'in_progress',
          healthScore: pickNumber(item.project?.healthScore, item.project?.health_score) || 0,
          healthLevel:
            (pickString(item.project?.healthLevel, item.project?.health_level) as any) || 'healthy',
          openRiskCount: pickNumber(item.project?.openRiskCount, item.project?.open_risk_count) || 0,
        },
        targetTitle: pickString(item.targetTitle, item.target_title) || null,
        targetSummary: pickString(item.targetSummary, item.target_summary) || null,
      }))
    : [];
}

export async function queryProjectOverview(
  projectUuid: string,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectOverviewData | Record<string, unknown>>>(
    `/bff/project-center/projects/${projectUuid}/overview`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );
  return normalizeProjectOverviewData(result.data);
}

export async function queryProjectPlan(
  projectUuid: string,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectPlanData | Record<string, unknown>>>(
    `/bff/project-center/projects/${projectUuid}/plan`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );
  return normalizeProjectPlanData(result.data);
}

export async function createProjectPlanStage(
  projectUuid: string,
  body: CreateProjectPlanStagePayload,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectPlanStageItem | Record<string, unknown>>>(
    `/bff/project-center/projects/${projectUuid}/plan/stages`,
    {
      method: 'POST',
      data: body,
      ...(options || {}),
    },
  );
  return normalizeProjectPlanStage(result.data);
}

export async function updateProjectPlanStage(
  projectUuid: string,
  stageUuid: string,
  body: UpdateProjectPlanStagePayload,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectPlanStageItem | Record<string, unknown>>>(
    `/bff/project-center/projects/${projectUuid}/plan/stages/${stageUuid}`,
    {
      method: 'PATCH',
      data: body,
      ...(options || {}),
    },
  );
  return normalizeProjectPlanStage(result.data);
}

export async function deleteProjectPlanStage(
  projectUuid: string,
  stageUuid: string,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<boolean>>(
    `/bff/project-center/projects/${projectUuid}/plan/stages/${stageUuid}`,
    {
      method: 'DELETE',
      ...(options || {}),
    },
  );
  return result.data;
}

export async function createProjectPlanMilestone(
  projectUuid: string,
  body: CreateProjectPlanMilestonePayload,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectPlanMilestoneItem | Record<string, unknown>>>(
    `/bff/project-center/projects/${projectUuid}/plan/milestones`,
    {
      method: 'POST',
      data: body,
      ...(options || {}),
    },
  );
  return normalizeProjectPlanMilestone(result.data);
}

export async function updateProjectPlanMilestone(
  projectUuid: string,
  milestoneUuid: string,
  body: UpdateProjectPlanMilestonePayload,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectPlanMilestoneItem | Record<string, unknown>>>(
    `/bff/project-center/projects/${projectUuid}/plan/milestones/${milestoneUuid}`,
    {
      method: 'PATCH',
      data: body,
      ...(options || {}),
    },
  );
  return normalizeProjectPlanMilestone(result.data);
}

export async function deleteProjectPlanMilestone(
  projectUuid: string,
  milestoneUuid: string,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<boolean>>(
    `/bff/project-center/projects/${projectUuid}/plan/milestones/${milestoneUuid}`,
    {
      method: 'DELETE',
      ...(options || {}),
    },
  );
  return result.data;
}

export async function updateProjectPlanMilestoneStatus(
  projectUuid: string,
  milestoneUuid: string,
  status: string,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectPlanMilestoneItem | Record<string, unknown>>>(
    `/bff/project-center/projects/${projectUuid}/plan/milestones/${milestoneUuid}/status/${status}`,
    { method: 'PATCH', ...(options || {}) },
  );
  return normalizeProjectPlanMilestone(result.data);
}

export async function updateProjectTaskDelay(
  projectUuid: string,
  taskUuid: string,
  body: { isDelayed?: boolean; delayReason?: string },
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectTaskItem>>(
    `/bff/project-center/projects/${projectUuid}/plan/tasks/${taskUuid}/delay`,
    {
      method: 'PATCH',
      data: body,
      ...(options || {}),
    },
  );
  return normalizeProjectTaskItem(result.data);
}

export async function queryProjectFocusItems(projectUuid: string, options?: ApiRequestOptions) {
  const result = await request<ApiResponse<ProjectFocusItem[] | Record<string, unknown>[]>>(
    `/bff/project-center/projects/${projectUuid}/focus-items`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );
  return Array.isArray(result.data) ? result.data.map(normalizeProjectFocusItem) : [];
}

export async function createProjectFocusItem(
  projectUuid: string,
  body: CreateProjectFocusItemPayload,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectFocusItem | Record<string, unknown>>>(
    `/bff/project-center/projects/${projectUuid}/focus-items`,
    {
      method: 'POST',
      data: body,
      ...(options || {}),
    },
  );
  return normalizeProjectFocusItem(result.data);
}

export async function updateProjectFocusItem(
  projectUuid: string,
  focusUuid: string,
  body: UpdateProjectFocusItemPayload,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectFocusItem | Record<string, unknown>>>(
    `/bff/project-center/projects/${projectUuid}/focus-items/${focusUuid}`,
    {
      method: 'PATCH',
      data: body,
      ...(options || {}),
    },
  );
  return normalizeProjectFocusItem(result.data);
}

export async function confirmProjectFocusItem(
  projectUuid: string,
  focusUuid: string,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectFocusItem | Record<string, unknown>>>(
    `/bff/project-center/projects/${projectUuid}/focus-items/${focusUuid}/confirm`,
    {
      method: 'PATCH',
      ...(options || {}),
    },
  );
  return normalizeProjectFocusItem(result.data);
}

export async function reorderProjectFocusItem(
  projectUuid: string,
  focusUuid: string,
  priority: number,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectFocusItem | Record<string, unknown>>>(
    `/bff/project-center/projects/${projectUuid}/focus-items/${focusUuid}/reorder`,
    {
      method: 'PATCH',
      data: { priority },
      ...(options || {}),
    },
  );
  return normalizeProjectFocusItem(result.data);
}

export async function removeProjectFocusItem(
  projectUuid: string,
  focusUuid: string,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectFocusItem | Record<string, unknown>>>(
    `/bff/project-center/projects/${projectUuid}/focus-items/${focusUuid}/remove`,
    {
      method: 'PATCH',
      ...(options || {}),
    },
  );
  return normalizeProjectFocusItem(result.data);
}

export async function queryProjectWeeklyReview(
  projectUuid: string,
  params?: QueryProjectWeeklyReviewParams,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectWeeklyReviewData | Record<string, unknown>>>(
    `/bff/project-center/projects/${projectUuid}/weekly-review`,
    {
      method: 'GET',
      params: params ? { ...(params as Record<string, unknown>) } : undefined,
      ...(options || {}),
    },
  );
  return result.data as ProjectWeeklyReviewData;
}

export async function queryProjectReports(
  projectUuid: string,
  params?: QueryProjectReportsParams,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectReportListResult | Record<string, unknown>>>(
    `/bff/project-center/projects/${projectUuid}/reports`,
    {
      method: 'GET',
      params: params ? { ...(params as Record<string, unknown>) } : undefined,
      ...(options || {}),
    },
  );
  const rawData = result.data as any;
  return {
    items: Array.isArray(rawData?.items)
      ? rawData.items.map(normalizeProjectReportItem)
      : [],
    total: pickNumber(rawData?.total) || 0,
    page: pickNumber(rawData?.page) || 1,
    pageSize: pickNumber(rawData?.pageSize, rawData?.page_size) || 20,
  } satisfies ProjectReportListResult;
}

export async function getProjectReport(
  projectUuid: string,
  reportUuid: string,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectReportItem | Record<string, unknown>>>(
    `/bff/project-center/projects/${projectUuid}/reports/${reportUuid}`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );
  return normalizeProjectReportItem(result.data);
}

export async function createProjectReport(
  projectUuid: string,
  body: CreateProjectReportPayload,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectReportItem | Record<string, unknown>>>(
    `/bff/project-center/projects/${projectUuid}/reports`,
    {
      method: 'POST',
      data: body,
      ...(options || {}),
    },
  );
  return normalizeProjectReportItem(result.data);
}

export async function updateProjectReport(
  projectUuid: string,
  reportUuid: string,
  body: UpdateProjectReportPayload,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectReportItem | Record<string, unknown>>>(
    `/bff/project-center/projects/${projectUuid}/reports/${reportUuid}`,
    {
      method: 'PATCH',
      data: body,
      ...(options || {}),
    },
  );
  return normalizeProjectReportItem(result.data);
}

export async function submitProjectReport(
  projectUuid: string,
  reportUuid: string,
  body?: UpdateProjectReportPayload,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectReportItem | Record<string, unknown>>>(
    `/bff/project-center/projects/${projectUuid}/reports/${reportUuid}`,
    {
      method: 'PATCH',
      data: {
        ...(body || {}),
        status: 'submitted',
      },
      ...(options || {}),
    },
  );
  return normalizeProjectReportItem(result.data);
}

export async function streamGenerateProjectReport(
  projectUuid: string,
  body: {
    reportType: ProjectReportType;
    periodKey: string;
    periodStart: string;
    periodEnd: string;
    reportProfileUuid?: string;
    supplement?: {
      audience?: string;
      focusPoints?: string[];
      specialNotes?: string;
      risksToHighlight?: string[];
      nextSteps?: string[];
    };
  },
  handlers: {
    onChunk: (chunk: string) => void;
    onComplete?: (payload: {
      modelName?: string | null;
      generatedAt?: string;
      reportProfileUuid?: string | null;
      inputSnapshot?: Record<string, unknown>;
    }) => void;
  },
) {
  const token = (await ensureValidAccessToken()) || getAccessToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  const tenantUuid = readStoredTenantUuid();
  if (tenantUuid) {
    headers['x-tenant-id'] = tenantUuid;
  }
  const organizationUuid = readStoredOrganizationUuid();
  if (organizationUuid) {
    headers['x-organization-id'] = organizationUuid;
  }

  const response = await fetch(`${BizAppConfig.appApiUrl || ''}/bff/project-center/projects/${projectUuid}/reports/generate`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  if (!response.ok || !response.body) {
    throw new Error(`生成项目汇报失败 (${response.status})`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';

  while (true) {
    const { value, done } = await reader.read();
    if (done) {
      break;
    }
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';

    for (const line of lines) {
      if (!line.trim()) {
        continue;
      }
      const payload = JSON.parse(line) as
        | { type: 'chunk'; content: string }
        | {
            type: 'complete';
            modelName?: string | null;
            generatedAt?: string;
            reportProfileUuid?: string | null;
            inputSnapshot?: Record<string, unknown>;
          };
      if (payload.type === 'chunk') {
        handlers.onChunk(payload.content || '');
      } else if (payload.type === 'complete') {
        handlers.onComplete?.({
          modelName: payload.modelName,
          generatedAt: payload.generatedAt,
          reportProfileUuid: payload.reportProfileUuid,
          inputSnapshot: payload.inputSnapshot,
        });
      }
    }
  }
}

export const queryProjectWeeklyReports = queryProjectReports;
export const getProjectWeeklyReport = getProjectReport;
export const createProjectWeeklyReport = createProjectReport;
export const updateProjectWeeklyReport = updateProjectReport;
export const submitProjectWeeklyReport = submitProjectReport;

export async function createProjectReview(
  projectUuid: string,
  body: {
    title?: string;
    reviewType?: ProjectReviewItem['reviewType'];
    periodStart?: string;
    periodEnd?: string;
    participantMemberUuids?: string[];
    summary?: string;
    content?: string;
  },
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectReviewItem>>(
    `/bff/project-center/projects/${projectUuid}/reviews`,
    {
      method: 'POST',
      data: body,
      ...(options || {}),
    },
  );
  return result.data;
}

export async function queryProjectReviews(
  projectUuid: string,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectReviewItem[]>>(
    `/bff/project-center/projects/${projectUuid}/reviews`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );
  return result.data;
}

export async function queryProjectReview(
  projectUuid: string,
  reviewUuid: string,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectReviewItem>>(
    `/bff/project-center/projects/${projectUuid}/reviews/${reviewUuid}`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );
  return result.data;
}

export async function generateProjectReview(
  projectUuid: string,
  reviewUuid: string,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectReviewItem>>(
    `/bff/project-center/projects/${projectUuid}/reviews/${reviewUuid}/generate`,
    {
      method: 'POST',
      ...(options || {}),
    },
  );
  return result.data;
}

export async function updateProjectReview(
  projectUuid: string,
  reviewUuid: string,
  body: Partial<Pick<ProjectReviewItem, 'title' | 'reviewType' | 'status' | 'periodStart' | 'periodEnd' | 'participantMemberUuids' | 'summary' | 'content' | 'structuredContent'>>,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectReviewItem>>(
    `/bff/project-center/projects/${projectUuid}/reviews/${reviewUuid}`,
    {
      method: 'PATCH',
      data: body,
      ...(options || {}),
    },
  );
  return result.data;
}

export async function confirmProjectReview(
  projectUuid: string,
  reviewUuid: string,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectReviewItem>>(
    `/bff/project-center/projects/${projectUuid}/reviews/${reviewUuid}/confirm`,
    {
      method: 'POST',
      ...(options || {}),
    },
  );
  return result.data;
}

export async function generateProjectReviewMaterials(
  projectUuid: string,
  reviewUuid: string,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectReviewItem>>(
    `/bff/project-center/projects/${projectUuid}/reviews/${reviewUuid}/generate-materials`,
    {
      method: 'POST',
      ...(options || {}),
    },
  );
  return result.data;
}

export async function generateProjectReviewConclusion(
  projectUuid: string,
  reviewUuid: string,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectReviewItem>>(
    `/bff/project-center/projects/${projectUuid}/reviews/${reviewUuid}/generate-conclusion`,
    {
      method: 'POST',
      ...(options || {}),
    },
  );
  return result.data;
}

export async function generateProjectReviewActions(
  projectUuid: string,
  reviewUuid: string,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectReviewItem>>(
    `/bff/project-center/projects/${projectUuid}/reviews/${reviewUuid}/generate-actions`,
    {
      method: 'POST',
      ...(options || {}),
    },
  );
  return result.data;
}

export async function queryProjectReviewActions(
  projectUuid: string,
  reviewUuid: string,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectReviewActionItem[]>>(
    `/bff/project-center/projects/${projectUuid}/reviews/${reviewUuid}/actions`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );
  return result.data;
}

export async function createProjectReviewAction(
  projectUuid: string,
  reviewUuid: string,
  body: Partial<ProjectReviewActionItem>,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectReviewActionItem>>(
    `/bff/project-center/projects/${projectUuid}/reviews/${reviewUuid}/actions`,
    {
      method: 'POST',
      data: body,
      ...(options || {}),
    },
  );
  return result.data;
}

export async function updateProjectReviewAction(
  projectUuid: string,
  reviewUuid: string,
  actionUuid: string,
  body: Partial<ProjectReviewActionItem>,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectReviewActionItem>>(
    `/bff/project-center/projects/${projectUuid}/reviews/${reviewUuid}/actions/${actionUuid}`,
    {
      method: 'PATCH',
      data: body,
      ...(options || {}),
    },
  );
  return result.data;
}

export async function queryProjectAcceptances(
  projectUuid: string,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectAcceptanceItem[]>>(
    `/bff/project-center/projects/${projectUuid}/acceptances`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );
  return result.data;
}

export async function queryProjectAcceptance(
  projectUuid: string,
  acceptanceUuid: string,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectAcceptanceItem>>(
    `/bff/project-center/projects/${projectUuid}/acceptances/${acceptanceUuid}`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );
  return result.data;
}

export async function createProjectAcceptance(
  projectUuid: string,
  body: Partial<ProjectAcceptanceCreatePayload>,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectAcceptanceItem>>(
    `/bff/project-center/projects/${projectUuid}/acceptances`,
    {
      method: 'POST',
      data: body,
      ...(options || {}),
    },
  );
  return result.data;
}

export async function updateProjectAcceptance(
  projectUuid: string,
  acceptanceUuid: string,
  body: Partial<ProjectAcceptanceCreatePayload>,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectAcceptanceItem>>(
    `/bff/project-center/projects/${projectUuid}/acceptances/${acceptanceUuid}`,
    {
      method: 'PATCH',
      data: body,
      ...(options || {}),
    },
  );
  return result.data;
}

export async function deleteProjectAcceptance(
  projectUuid: string,
  acceptanceUuid: string,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<boolean>>(
    `/bff/project-center/projects/${projectUuid}/acceptances/${acceptanceUuid}`,
    {
      method: 'DELETE',
      ...(options || {}),
    },
  );
  return result.data;
}

export async function createProjectAcceptanceRectifyItem(
  projectUuid: string,
  acceptanceUuid: string,
  body: {
    content: string;
    ownerUuid?: string;
    deadline?: string;
    remark?: string;
    sortOrder?: number;
  },
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectAcceptanceItem>>(
    `/bff/project-center/projects/${projectUuid}/acceptances/${acceptanceUuid}/rectify-items`,
    {
      method: 'POST',
      data: body,
      ...(options || {}),
    },
  );
  return result.data;
}

export async function updateProjectAcceptanceRectifyItem(
  projectUuid: string,
  acceptanceUuid: string,
  itemUuid: string,
  body: {
    content?: string;
    ownerUuid?: string;
    deadline?: string;
    remark?: string;
    sortOrder?: number;
  },
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectAcceptanceItem>>(
    `/bff/project-center/projects/${projectUuid}/acceptances/${acceptanceUuid}/rectify-items/${itemUuid}`,
    {
      method: 'PATCH',
      data: body,
      ...(options || {}),
    },
  );
  return result.data;
}

export async function removeProjectAcceptanceRectifyItem(
  projectUuid: string,
  acceptanceUuid: string,
  itemUuid: string,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectAcceptanceItem>>(
    `/bff/project-center/projects/${projectUuid}/acceptances/${acceptanceUuid}/rectify-items/${itemUuid}`,
    {
      method: 'DELETE',
      ...(options || {}),
    },
  );
  return result.data;
}

export async function createProjectAcceptanceFollowPlan(
  projectUuid: string,
  acceptanceUuid: string,
  body: {
    content: string;
    ownerUuid?: string;
    planDate?: string;
    remark?: string;
    sortOrder?: number;
  },
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectAcceptanceItem>>(
    `/bff/project-center/projects/${projectUuid}/acceptances/${acceptanceUuid}/follow-plans`,
    {
      method: 'POST',
      data: body,
      ...(options || {}),
    },
  );
  return result.data;
}

export async function updateProjectAcceptanceFollowPlan(
  projectUuid: string,
  acceptanceUuid: string,
  itemUuid: string,
  body: {
    content?: string;
    ownerUuid?: string;
    planDate?: string;
    remark?: string;
    sortOrder?: number;
  },
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectAcceptanceItem>>(
    `/bff/project-center/projects/${projectUuid}/acceptances/${acceptanceUuid}/follow-plans/${itemUuid}`,
    {
      method: 'PATCH',
      data: body,
      ...(options || {}),
    },
  );
  return result.data;
}

export async function removeProjectAcceptanceFollowPlan(
  projectUuid: string,
  acceptanceUuid: string,
  itemUuid: string,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectAcceptanceItem>>(
    `/bff/project-center/projects/${projectUuid}/acceptances/${acceptanceUuid}/follow-plans/${itemUuid}`,
    {
      method: 'DELETE',
      ...(options || {}),
    },
  );
  return result.data;
}

export async function addProjectAcceptanceAttachment(
  projectUuid: string,
  acceptanceUuid: string,
  body: Record<string, unknown>,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectAcceptanceItem>>(
    `/bff/project-center/projects/${projectUuid}/acceptances/${acceptanceUuid}/attachments`,
    {
      method: 'POST',
      data: body,
      ...(options || {}),
    },
  );
  return result.data;
}

export async function removeProjectAcceptanceAttachment(
  projectUuid: string,
  acceptanceUuid: string,
  fileKey: string,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectAcceptanceItem>>(
    `/bff/project-center/projects/${projectUuid}/acceptances/${acceptanceUuid}/attachments/${fileKey}`,
    {
      method: 'PATCH',
      ...(options || {}),
    },
  );
  return result.data;
}

export async function submitProjectAcceptance(
  projectUuid: string,
  acceptanceUuid: string,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectAcceptanceItem>>(
    `/bff/project-center/projects/${projectUuid}/acceptances/${acceptanceUuid}/submit`,
    {
      method: 'POST',
      ...(options || {}),
    },
  );
  return result.data;
}

export async function approveProjectAcceptance(
  projectUuid: string,
  acceptanceUuid: string,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectAcceptanceItem>>(
    `/bff/project-center/projects/${projectUuid}/acceptances/${acceptanceUuid}/approve`,
    {
      method: 'POST',
      ...(options || {}),
    },
  );
  return result.data;
}

export async function rejectProjectAcceptance(
  projectUuid: string,
  acceptanceUuid: string,
  rejectedReason: string,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectAcceptanceItem>>(
    `/bff/project-center/projects/${projectUuid}/acceptances/${acceptanceUuid}/reject`,
    {
      method: 'POST',
      data: { rejectedReason },
      ...(options || {}),
    },
  );
  return result.data;
}

export async function queryProjectCommunications(
  projectUuid: string,
  params?: QueryProjectCommunicationsParams,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectCommunicationItem[]>>(
    `/bff/project-center/projects/${projectUuid}/communications`,
    {
      method: 'GET',
      params: params
        ? {
            impactLevel: params.impactLevel,
            isKeyCommunication:
              params.isKeyCommunication === undefined ? undefined : String(params.isKeyCommunication),
          }
        : undefined,
      ...(options || {}),
    },
  );
  return result.data;
}

export async function queryProjectCommunication(
  projectUuid: string,
  communicationUuid: string,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectCommunicationItem>>(
    `/bff/project-center/projects/${projectUuid}/communications/${communicationUuid}`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );
  return result.data;
}

export async function createProjectCommunication(
  projectUuid: string,
  body: Partial<ProjectCommunicationPayload>,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectCommunicationItem>>(
    `/bff/project-center/projects/${projectUuid}/communications`,
    {
      method: 'POST',
      data: body,
      ...(options || {}),
    },
  );
  return result.data;
}

export async function updateProjectCommunication(
  projectUuid: string,
  communicationUuid: string,
  body: Partial<ProjectCommunicationPayload>,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectCommunicationItem>>(
    `/bff/project-center/projects/${projectUuid}/communications/${communicationUuid}`,
    {
      method: 'PATCH',
      data: body,
      ...(options || {}),
    },
  );
  return result.data;
}

export async function queryProjectFileTab(
  projectUuid: string,
  params?: {
    folderUuid?: string | null;
  },
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectReadonlyTabData>>(
    `/bff/project-center/projects/${projectUuid}/files`,
    {
      method: 'GET',
      params: params?.folderUuid ? { folderUuid: params.folderUuid } : undefined,
      ...(options || {}),
    },
  );
  return normalizeProjectReadonlyTabData(result.data);
}

export async function createProjectFileRelation(
  projectUuid: string,
  body: {
    fileKey: string;
    fileName?: string;
    contentType?: string;
    fileSize?: number;
    folderUuid?: string | null;
  },
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<Record<string, unknown>>>(
    `/bff/project-center/projects/${projectUuid}/files`,
    {
      method: 'POST',
      data: body,
      ...(options || {}),
    },
  );
  return result.data;
}

export async function createProjectFileFolder(
  projectUuid: string,
  body: {
    folderName: string;
    parentFolderUuid?: string | null;
    sortOrder?: number;
  },
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<Record<string, unknown>>>(
    `/bff/project-center/projects/${projectUuid}/files/folders`,
    {
      method: 'POST',
      data: body,
      ...(options || {}),
    },
  );
  return result.data;
}

export async function updateProjectFileFolder(
  projectUuid: string,
  folderUuid: string,
  body: {
    folderName?: string;
    parentFolderUuid?: string | null;
    sortOrder?: number;
  },
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<Record<string, unknown>>>(
    `/bff/project-center/projects/${projectUuid}/files/folders/${folderUuid}`,
    {
      method: 'PATCH',
      data: body,
      ...(options || {}),
    },
  );
  return result.data;
}

export async function removeProjectFileFolder(
  projectUuid: string,
  folderUuid: string,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<boolean>>(
    `/bff/project-center/projects/${projectUuid}/files/folders/${folderUuid}`,
    {
      method: 'DELETE',
      ...(options || {}),
    },
  );
  return result.data;
}

export async function moveProjectFileToFolder(
  projectUuid: string,
  fileRelationUuid: string,
  body: {
    folderUuid?: string | null;
  },
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<Record<string, unknown>>>(
    `/bff/project-center/projects/${projectUuid}/files/${fileRelationUuid}/folder`,
    {
      method: 'PATCH',
      data: body,
      ...(options || {}),
    },
  );
  return result.data;
}

export async function removeProjectFileRelation(
  projectUuid: string,
  fileRelationUuid: string,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<boolean>>(
    `/bff/project-center/projects/${projectUuid}/files/${fileRelationUuid}`,
    {
      method: 'DELETE',
      ...(options || {}),
    },
  );
  return result.data;
}

export async function uploadCommonFile(
  body: CommonUploadFilePayload,
  options?: ApiRequestOptions,
) {
  const formData = new FormData();
  formData.append('bizType', body.bizType);
  formData.append('file', body.file);
  if (body.objectKey) {
    formData.append('objectKey', body.objectKey);
  }
  if (body.fileName) {
    formData.append('fileName', body.fileName);
  }

  const result = await request<ApiResponse<CommonUploadFileResult>>('/bff/common/uploads', {
    method: 'POST',
    data: formData,
    ...(options || {}),
  });
  return result.data;
}

export async function getCommonFilePreviewUrl(
  objectKey: string,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<CommonSignedUrlResult>>(
    '/bff/common/uploads/preview-url',
    {
      method: 'GET',
      params: {
        objectKey,
      },
      ...(options || {}),
    },
  );
  return result.data;
}

export async function getCommonFileDownloadUrl(
  objectKey: string,
  fileName?: string,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<CommonSignedUrlResult>>(
    '/bff/common/uploads/download-url',
    {
      method: 'GET',
      params: {
        objectKey,
        fileName,
      },
      ...(options || {}),
    },
  );
  return result.data;
}

export async function refreshProjectRisks(
  projectUuid: string,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectRiskItem[]>>(
    `/bff/project-center/projects/${projectUuid}/risks/refresh`,
    {
      method: 'POST',
      ...(options || {}),
    },
  );
  return result.data;
}

export async function resolveProjectRisk(
  projectUuid: string,
  riskUuid: string,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectRiskItem | null>>(
    `/bff/project-center/projects/${projectUuid}/risks/${riskUuid}/resolve`,
    {
      method: 'PATCH',
      ...(options || {}),
    },
  );
  return result.data;
}

export async function createProjectAiAnalysis(
  projectUuid: string,
  body: CreateProjectAiAnalysisPayload,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectAiAnalysisItem>>(
    `/bff/project-center/projects/${projectUuid}/ai-analysis`,
    {
      method: 'POST',
      data: body,
      ...(options || {}),
    },
  );
  return result.data;
}

export async function queryProjectAiAnalyses(
  projectUuid: string,
  options?: ApiRequestOptions,
) {
  const result = await request<ApiResponse<ProjectAiAnalysisItem[]>>(
    `/bff/project-center/projects/${projectUuid}/ai-analysis`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );
  return result.data;
}

export async function queryProjectDashboard(options?: ApiRequestOptions) {
  const result = await request<ApiResponse<any>>(
    '/bff/project-center/dashboard',
    {
      method: 'GET',
      ...(options || {}),
    },
  );
  return normalizeProjectDashboardData(result.data);
}

export async function queryProjectDashboardWithFallback(options?: ApiRequestOptions) {
  try {
    return await queryProjectDashboard({
      skipErrorHandler: true,
      ...(options || {}),
    });
  } catch {
    const [projectPage, resourcePage] = await Promise.all([
      queryProjects(
        {
          page: 1,
          pageSize: 100,
        },
        options,
      ),
      queryResourceCenter(
        {
          page: 1,
          pageSize: 100,
        },
        options,
      ),
    ]);

    const projects = projectPage.items;
    const resources = resourcePage.items;
    const totalRisks = projects.reduce(
      (sum: number, item) => sum + Number(item.openRiskCount || 0),
      0,
    );
    const highRisks = projects.reduce(
      (sum: number, item) => sum + Number(item.highRiskCount || 0),
      0,
    );

    return {
      projectStats: {
        total: projects.length,
        inProgress: projects.filter((item) => item.status === 'in_progress').length,
        overdue: projects.filter((item) => Boolean(item.isOverdue)).length,
        highRisk: projects.filter((item) => Number(item.highRiskCount || 0) > 0).length,
        completed: projects.filter((item) => item.status === 'completed').length,
      },
      resourceStats: {
        totalResources: resources.length,
        overloaded: resources.filter((item) => item.loadPercent >= 100).length,
        idle: resources.filter((item) => item.loadPercent <= 50).length,
        averageLoadPercent:
          resources.length > 0
            ? Number(
                (
                  resources.reduce(
                    (sum: number, item) => sum + Number(item.loadPercent || 0),
                    0,
                  ) /
                  resources.length
                ).toFixed(2),
              )
            : 0,
      },
      riskStats: {
        totalRisks,
        highRisks,
      },
      healthDistribution: {
        healthy: projects.filter((item) => item.healthLevel === 'healthy').length,
        attention: projects.filter((item) => item.healthLevel === 'attention').length,
        risk: projects.filter((item) => item.healthLevel === 'risk').length,
        critical: projects.filter((item) => item.healthLevel === 'critical').length,
      },
      highRiskProjects: projects
        .filter(
          (item) =>
            Number(item.highRiskCount || 0) > 0 ||
            Number(item.openRiskCount || 0) > 0 ||
            Boolean(item.isOverdue),
        )
        .slice(0, 10)
        .map((item) => ({
          uuid: item.uuid,
          name: item.name,
          healthScore: item.healthScore,
          healthLevel: item.healthLevel,
          status: item.status,
          endDate: item.endDate,
          openRiskCount: Number(item.openRiskCount || 0),
          highRiskCount: Number(item.highRiskCount || 0),
        })),
      resourceRanking: resources
        .slice()
        .sort(
          (left, right) =>
            Number(right.loadPercent || 0) - Number(left.loadPercent || 0),
        )
        .slice(0, 10)
        .map((item) => ({
          resourceUuid: item.memberUuid,
          loadPercent: item.loadPercent,
          loadStatus: item.loadStatus,
          taskCount: item.currentTaskCount,
          estimatedHours: item.estimatedHours,
        })),
      aiRiskAlerts: [],
      recentActivities: [],
    } satisfies ProjectDashboardData;
  }
}

export async function queryProjectWorkbench(options?: ApiRequestOptions) {
  const result = await request<ApiResponse<any>>(
    '/bff/project-center/workbench',
    {
      method: 'GET',
      ...(options || {}),
    },
  );
  return normalizeProjectWorkbenchData(result.data);
}

export async function queryProjectCalendar(
  params?: QueryProjectCalendarParams,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResponse<any>>('/bff/project-center/calendar', {
    method: 'GET',
    params: toQueryParams(params),
    ...(options || {}),
  });

  return {
    range: {
      startDate: pickString(response?.data?.range?.startDate, response?.data?.range?.start_date) || '',
      endDate: pickString(response?.data?.range?.endDate, response?.data?.range?.end_date) || '',
      viewMode:
        (pickString(response?.data?.range?.viewMode, response?.data?.range?.view_mode) as
          | 'month'
          | 'week'
          | undefined) || 'month',
    },
    filters: {
      memberUuid:
        pickString(response?.data?.filters?.memberUuid, response?.data?.filters?.member_uuid) ||
        null,
      projectUuid:
        pickString(response?.data?.filters?.projectUuid, response?.data?.filters?.project_uuid) ||
        null,
    },
    calendarEvents: Array.isArray(response?.data?.calendarEvents)
      ? response.data.calendarEvents.map((item: any) => ({
          id: pickString(item.id) || '',
          eventType:
            (pickString(item.eventType, item.event_type) as
              | 'task'
              | 'milestone'
              | 'project_node'
              | undefined) || 'task',
          title: pickString(item.title, item.name) || '未命名事件',
          projectUuid: pickString(item.projectUuid, item.project_uuid) || null,
          projectName: pickString(item.projectName, item.project_name) || null,
          taskUuid: pickString(item.taskUuid, item.task_uuid) || null,
          status:
            (pickString(item.status) as ProjectTaskItem['status'] | undefined) || null,
          date: pickString(item.date) || null,
          startDate: pickString(item.startDate, item.start_date) || null,
          endDate: pickString(item.endDate, item.end_date) || null,
          isAllDay: Boolean(item.isAllDay ?? item.is_all_day ?? true),
          isRange: Boolean(item.isRange ?? item.is_range ?? false),
          priority:
            (pickString(item.priority) as ProjectItem['priority'] | undefined) || null,
          isOverdue: Boolean(item.isOverdue ?? item.is_overdue),
          description: pickString(item.description) || null,
          meta: item.meta || null,
        }))
      : [],
    selectedDateDetail: {
      date:
        pickString(
          response?.data?.selectedDateDetail?.date,
          response?.data?.selectedDateDetail?.selectedDate,
        ) || '',
      tasks: Array.isArray(response?.data?.selectedDateDetail?.tasks)
        ? response.data.selectedDateDetail.tasks
        : [],
      milestones: Array.isArray(response?.data?.selectedDateDetail?.milestones)
        ? response.data.selectedDateDetail.milestones
        : [],
      projectNodes: Array.isArray(response?.data?.selectedDateDetail?.projectNodes)
        ? response.data.selectedDateDetail.projectNodes
        : [],
    },
    todayFocus: {
      dueToday: Array.isArray(response?.data?.todayFocus?.dueToday)
        ? response.data.todayFocus.dueToday
        : [],
      startingToday: Array.isArray(response?.data?.todayFocus?.startingToday)
        ? response.data.todayFocus.startingToday
        : [],
      inProgressToday: Array.isArray(response?.data?.todayFocus?.inProgressToday)
        ? response.data.todayFocus.inProgressToday
        : [],
    },
  } satisfies ProjectCalendarData;
}

export async function queryProjectWorkbenchWithFallback(
  memberUuid: string,
  options?: ApiRequestOptions,
) {
  try {
    return await queryProjectWorkbench({
      skipErrorHandler: true,
      ...(options || {}),
    });
  } catch {
    const [taskPage, projectPage] = await Promise.all([
      queryMyTasks(
        {
          assigneeUuid: memberUuid,
          unfinishedOnly: false,
          sortBy: 'endDate',
          sortOrder: 'asc',
          page: 1,
          pageSize: 100,
        },
        options,
      ),
      queryProjects(
        {
          memberUuid,
          page: 1,
          pageSize: 100,
        },
        options,
      ),
    ]);

    const myTasks = taskPage.items.slice(0, 6);
    const myProjects = projectPage.items.slice(0, 6).map((item) => ({
      ...item,
      myProjectRole:
        item.ownerUuid === memberUuid ? ('owner_member' as const) : ('member' as const),
    }));
    const overdueCount = myTasks.filter(
      (item) => item.status !== 'done' && Boolean(item.isOverdue),
    ).length;

    return {
      todoSummary: {
        total: taskPage.total,
        todo: myTasks.filter((item) => item.status === 'todo').length,
        inProgress: myTasks.filter((item) => item.status === 'in_progress').length,
        review: myTasks.filter((item) => item.status === 'review').length,
        overdue: overdueCount,
      },
      projectSummary: {
        participatingCount: projectPage.total,
      },
      myTasks,
      myProjects,
      todaySchedule: {
        date: new Date().toISOString(),
        items: [],
        placeholderText: '今日暂无已接入的日程数据',
      },
      recentActivities: [],
    } satisfies ProjectWorkbenchData;
  }
}

export async function queryGlobalKanbanWithFallback(
  params?: QueryGlobalKanbanParams,
  options?: ApiRequestOptions,
) {
  try {
    return await queryGlobalKanban(params, {
      skipErrorHandler: true,
      ...(options || {}),
    });
  } catch {
    const projectPage = await queryProjects(
      {
        page: 1,
        pageSize: 100,
        keyword: params?.projectUuid ? undefined : params?.keyword,
      },
      options,
    );
    const taskGroups = await Promise.all(
      projectPage.items
        .filter((project) => !params?.projectUuid || project.uuid === params.projectUuid)
        .map(async (project) => ({
          project,
          tasks: await queryProjectTasks(
            project.uuid,
            {
              keyword: params?.keyword,
              assigneeUuid: params?.assigneeMemberUuid,
            },
            {
              skipErrorHandler: true,
              ...(options || {}),
            },
          ).catch(() => []),
        })),
    );

    const columns: GlobalKanbanColumn[] = taskStatusFallbackOrder.map((statusItem) => ({
      key: statusItem.key,
      name: statusItem.name,
      tasks: taskGroups.flatMap(({ project, tasks }) =>
        tasks
          .filter((task) => task.status === statusItem.key)
          .filter((task) =>
            params?.onlyMine && params?.assigneeMemberUuid
              ? task.assigneeUuid === params.assigneeMemberUuid
              : true,
          )
          .filter((task) => !params?.assigneeMemberUuid || task.assigneeUuid === params.assigneeMemberUuid)
          .filter((task) => {
            if (!params?.riskLevel) {
              return true;
            }
            const taskRiskLevel = normalizeGlobalTaskRiskLevel(
              Boolean(task.isOverdue) || Boolean(project.highRiskCount)
                ? 'high'
                : undefined,
            );
            return taskRiskLevel === params.riskLevel;
          })
          .map((task): GlobalKanbanTaskItem => ({
            ...task,
            projectUuid: task.projectUuid || project.uuid,
            projectName: project.name,
            projectCode: project.code,
            riskLevel: normalizeGlobalTaskRiskLevel(
              Boolean(task.isOverdue) || Boolean(project.highRiskCount)
                ? 'high'
                : undefined,
            ),
            isBlocked: false,
          })),
      ),
      total: 0,
    }));

    columns.forEach((item) => {
      item.total = item.tasks.length;
    });
    const allTasks = columns.flatMap((item) => item.tasks);

    return {
      columns,
      summary: {
        overdueCount: allTasks.filter((task) => Boolean(task.isOverdue)).length,
        blockedCount: allTasks.filter((task) => Boolean(task.isBlocked)).length,
        highRiskCount: allTasks.filter((task) => ['high', 'critical'].includes(task.riskLevel || '')).length,
      },
    } satisfies GlobalKanbanData;
  }
}

export async function queryGlobalGanttWithFallback(
  params?: QueryGlobalGanttParams,
  options?: ApiRequestOptions,
) {
  try {
    return await queryGlobalGantt(params, {
      skipErrorHandler: true,
      ...(options || {}),
    });
  } catch {
    const projectPage = await queryProjects(
      {
        page: 1,
        pageSize: 100,
      },
      options,
    );
    const ganttGroups = await Promise.all(
      projectPage.items.map(async (project) => ({
        project,
        gantt: await queryProjectGantt(project.uuid, {
          status: params?.status,
          skipErrorHandler: true,
          ...(options || {}),
        }).catch(() => ({ tasks: [], dependencies: [] })),
      })),
    );

    const tasks: GlobalGanttTaskItem[] = ganttGroups.flatMap(({ project, gantt }) =>
      gantt.tasks.map((task): GlobalGanttTaskItem => ({
        ...task,
        projectUuid: project.uuid,
        projectName: project.name,
        riskLevel: normalizeGlobalTaskRiskLevel(
          Boolean(task.isOverdue) || Boolean(project.highRiskCount)
            ? 'high'
            : undefined,
        ),
        isBlocked: false,
        loadPercent: null,
      })),
    );
    const filteredTasks = tasks.filter((task) => {
      if (params?.projectUuid && task.projectUuid !== params.projectUuid) {
        return false;
      }
      if (params?.assigneeMemberUuid && task.assigneeUuid !== params.assigneeMemberUuid) {
        return false;
      }
      if (params?.onlyCriticalPath && !task.isCriticalPath) {
        return false;
      }
      if (params?.onlyConflict && !task.isBlocked) {
        return false;
      }
      return true;
    });
    const taskIds = new Set(filteredTasks.map((task) => task.taskUuid || task.uuid || task.id));
    const dependencies = ganttGroups
      .flatMap(({ gantt }) => gantt.dependencies)
      .filter(
        (dependency) =>
          taskIds.has(dependency.sourceTaskId) && taskIds.has(dependency.targetTaskId),
      );

    return {
      tasks: filteredTasks,
      dependencies,
    } satisfies GlobalGanttData;
  }
}
